/* ============================================================
 * 60ft Ink — Mockup Builder (Phase 2)
 * ------------------------------------------------------------
 * - Loads print zone config (ft-print-zones.json)
 * - Konva stage renders garment template + artwork per side
 * - Artwork auto-fits into the zone on upload, then can be
 *   dragged and resized (aspect-locked) within the zone bounds,
 *   with snap-to-centre guides
 * - Effective print DPI check (warn / block thresholds), live
 *   during resize
 * - Uploads artwork to the R2 worker (/tools/upload)
 * - Writes design spec into hidden line item properties on the
 *   main product form:
 *     properties[_ft_design]          full JSON spec (renderer input)
 *     properties[Print Locations]     human-readable summary
 *     properties[Preview Colour]      colour used in preview
 * ============================================================ */

(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-ft-mockup-builder]';

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
      whenKonvaReady(function () {
        initBuilder(root).catch(function (err) {
          console.error('[ft-mb] init failed:', err);
          var loading = root.querySelector('[data-stage-loading]');
          if (loading) loading.textContent = 'Preview unavailable \u2014 please refresh, or contact us and we\u2019ll set your design up for you.';
        });
      });
    });
  });

  function whenKonvaReady(cb) {
    if (window.Konva) return cb();
    var tries = 0;
    var t = setInterval(function () {
      if (window.Konva) { clearInterval(t); cb(); }
      else if (++tries > 100) { clearInterval(t); console.error('[ft-mb] Konva failed to load'); }
    }, 100);
  }

  /* ---------------------------------------------------------- */

  async function initBuilder(root) {
    var configUrl = root.dataset.configUrl;
    var productKey = root.dataset.productKey;
    var uploadEndpoint = root.dataset.uploadEndpoint || 'https://ai-design-worker.60ft-ink.workers.dev/api/customer-upload';
    var requireDesign = root.dataset.requireDesign === 'true';
    var uploadPreviews = root.dataset.uploadPreviews === 'true';

    var config = await fetchJSON(configUrl);
    var productCfg = config.products && config.products[productKey];
    if (!productCfg) throw new Error('No print zone config for product key "' + productKey + '"');

    var dpiCfg = config.dpi || { warn_below: 150, block_below: 100 };

    /* ---------- state ---------- */
    var state = {
      colour: (productCfg.colours && productCfg.colours[0] && productCfg.colours[0].id) || '_default',
      activeSide: 'front',
      sides: {} // sideId -> { zoneId, art: {url,key,naturalW,naturalH,filename}, dpi, printMm:{w,h}, image }
    };
    Object.keys(productCfg.sides).forEach(function (sideId) {
      var zones = productCfg.sides[sideId].zones;
      var zoneIds = Object.keys(zones);
      // Default to the "large" zone (full_front/full_back) over a small
      // add-on like left_chest when a side has more than one option —
      // most customers dragging a full design in want the big print, and
      // Object.keys() order otherwise just reflects JSON declaration order.
      var defaultZoneId = zoneIds.find(function (id) { return id.indexOf('full_') === 0; }) || zoneIds[0];
      state.sides[sideId] = {
        zoneId: defaultZoneId,
        art: null,
        dpi: null,
        printMm: null,
        image: null,
        placement: null // {x,y,width,height} in canvas px once placed
      };
    });

    /* ---------- Konva stage ---------- */
    var stageWrap = root.querySelector('[data-stage]');
    var loadingEl = root.querySelector('[data-stage-loading]');
    var canvasW = productCfg.canvas.width;
    var canvasH = productCfg.canvas.height;

    var stage = new Konva.Stage({ container: stageWrap, width: canvasW, height: canvasH });
    var garmentLayer = new Konva.Layer({ listening: false });
    var artLayer = new Konva.Layer(); // draggable/transformable artwork lives here
    var guideLayer = new Konva.Layer({ listening: false });
    stage.add(garmentLayer, artLayer, guideLayer);

    fitStageToWrap();
    window.addEventListener('resize', debounce(fitStageToWrap, 150));

    // Resize handles only show while the artwork is "selected". Clicking
    // anywhere on the stage that isn't the artwork itself (empty canvas,
    // the zone guide, the garment) deselects it and hides them.
    var currentTransformer = null;
    stage.on('click tap', function (e) {
      if (e.target === stage && currentTransformer) {
        state.sides[state.activeSide].selected = false;
        currentTransformer.visible(false);
        artLayer.batchDraw();
      }
    });

    function fitStageToWrap() {
      var w = stageWrap.clientWidth || stageWrap.parentElement.clientWidth;
      if (!w) return;
      var scale = w / canvasW;
      stage.width(canvasW * scale);
      stage.height(canvasH * scale);
      stage.scale({ x: scale, y: scale });
      stage.batchDraw();
    }

    var templateNode = new Konva.Image({ x: 0, y: 0, width: canvasW, height: canvasH });
    garmentLayer.add(templateNode);

    // Offscreen stage used only for generating preview-snapshot uploads.
    // Never attached to the document, so building a snapshot for a side
    // the customer isn't looking at can never flash onto their screen —
    // it used to reuse the visible `stage`, flipping activeSide to render
    // each side in turn, which is exactly what caused that flash.
    var snapshotStage = new Konva.Stage({ container: document.createElement('div'), width: canvasW, height: canvasH });
    var snapshotGarmentLayer = new Konva.Layer({ listening: false });
    var snapshotArtLayer = new Konva.Layer({ listening: false });
    snapshotStage.add(snapshotGarmentLayer, snapshotArtLayer);
    var snapshotTemplateNode = new Konva.Image({ x: 0, y: 0, width: canvasW, height: canvasH });
    snapshotGarmentLayer.add(snapshotTemplateNode);

    var zoneGuide = new Konva.Rect({
      stroke: 'rgba(0,0,0,0.35)',
      dash: [8, 6],
      strokeWidth: 2,
      cornerRadius: 4,
      visible: false
    });
    guideLayer.add(zoneGuide);

    // How much of the maximum contain-fit size new artwork is placed at by
    // default — leaves headroom to resize larger, since a full "contain" fit
    // by definition already touches one edge of the zone.
    var DEFAULT_FIT_RATIO = 0.85;

    // Snap-to-centre guides, shown only while dragging/resizing near centre
    var SNAP_PX = 16; // canvas units (stage is scaled down visually via CSS)
    var centerGuideX = new Konva.Line({ stroke: '#e2711d', strokeWidth: 1.5, dash: [4, 4], visible: false, listening: false });
    var centerGuideY = new Konva.Line({ stroke: '#e2711d', strokeWidth: 1.5, dash: [4, 4], visible: false, listening: false });
    guideLayer.add(centerGuideX, centerGuideY);

    function showCenterGuides(z, snappedX, snappedY) {
      centerGuideX.points([z.rect.x + z.rect.width / 2, z.rect.y, z.rect.x + z.rect.width / 2, z.rect.y + z.rect.height]);
      centerGuideY.points([z.rect.x, z.rect.y + z.rect.height / 2, z.rect.x + z.rect.width, z.rect.y + z.rect.height / 2]);
      centerGuideX.visible(!!snappedX);
      centerGuideY.visible(!!snappedY);
      guideLayer.batchDraw();
    }

    function hideCenterGuides() {
      centerGuideX.visible(false);
      centerGuideY.visible(false);
      guideLayer.batchDraw();
    }

    /* ---------- UI refs ---------- */
    var sidePanels = root.querySelectorAll('[data-side-panel]');
    var statusDot = root.querySelector('[data-status-dot]');
    var statusText = root.querySelector('[data-status-text]');

    var templateCache = {};

    /* ---------- wizard state ---------- */
    // Maps our zone IDs to the pre-existing "Print Location Selector"
    // widget's checkbox ids (snippets/print-location-selector.liquid) —
    // that widget carries the real pricing (+$7.75 / +$10.95) and
    // independently gates Add to Cart, so a zone picked here has to be
    // reflected there or checkout stays blocked. Hoisted up here (was
    // previously declared much later, next to syncPrintLocationSelector)
    // so it's available for the order-builder pre-selection check below
    // too — moving a `var` object literal earlier in the same function
    // scope is safe since nothing above this point ever referenced it.
    var PLS_ZONE_MAP = {
      front: { left_chest: 'pls-front', full_front: 'pls-large-front' },
      back: { full_back: 'pls-back' },
      left_sleeve: { left_sleeve: 'pls-left-sleeve' },
      right_sleeve: { right_sleeve: 'pls-right-sleeve' }
    };

    // order-builder-ready dev pilot (2026-08-07): when the new unified
    // ft-order-builder widget is present on the page, print-location choice
    // already happened there — the wizard skips its own "would you like a
    // print on X?" yes/no steps entirely and goes straight to design steps
    // for whatever's already checked. Every other product (no
    // [data-ft-order-builder] on the page) keeps the original flow, byte
    // for byte. See /Users/rolandsoutar/.claude/plans/humble-wiggling-peach.md.
    var orderBuilderMode = !!document.querySelector('[data-ft-order-builder]');

    function preSelectedZoneForSide(sideId) {
      var zoneMap = PLS_ZONE_MAP[sideId];
      if (!zoneMap) return null;
      for (var zoneId in zoneMap) {
        if (!zoneMap.hasOwnProperty(zoneId)) continue;
        var checkbox = document.getElementById(zoneMap[zoneId]);
        if (checkbox && checkbox.checked) return zoneId;
      }
      return null;
    }

    // One decision per screen: one yes/no screen per side -> design each
    // chosen side -> review + add to cart. `sideIds`/`choiceSteps` are
    // fixed upfront from the config; `steps` gets the design/review tail
    // appended once the last side's yes/no choice is made. 'design:front'
    // / 'design:back' show the same panels the old tabbed layout used,
    // just one at a time instead of all at once.
    var sideIds = Object.keys(productCfg.sides);
    var choiceSteps, wizard;

    if (orderBuilderMode) {
      choiceSteps = []; // no yes/no steps — selection already happened above
      var preSelectedSides = [];
      sideIds.forEach(function (sideId) {
        var zoneId = preSelectedZoneForSide(sideId);
        if (zoneId) {
          preSelectedSides.push(sideId);
          state.sides[sideId].zoneId = zoneId;
        }
      });
      wizard = { currentIndex: 0, steps: computeWizardSteps(preSelectedSides), chosenSides: preSelectedSides, pendingSides: preSelectedSides.slice() };
    } else {
      choiceSteps = sideIds.map(function (id) { return 'sides:' + id; });
      wizard = { currentIndex: 0, steps: choiceSteps.slice(), chosenSides: [], pendingSides: [] };
    }

    // Declared here (not down near notifyWizardChange()'s own definition)
    // because goToWizardStep(0) — which now calls updateWizardNav(), which
    // now calls notifyWizardChange() — gets invoked further down in this
    // same init sequence, before this file's source order would otherwise
    // reach a `var` assignment placed later. `var` hoisting only hoists
    // the declaration (name exists, value undefined), not the assignment,
    // so a `.forEach` on this before this line actually runs throws —
    // hit this for real, "Cannot read properties of undefined (reading
    // 'forEach')", both on init and on every upload afterward.
    var wizardChangeListeners = [];

    // Copyright/ownership confirmation — required before Add to Cart
    // enables, recorded onto the order (not just a UI checkbox that
    // vanishes without a trace). Timestamp is captured once, at the
    // moment of confirming, not regenerated on every sync.
    var copyrightConfirmed = false;
    var copyrightConfirmedAt = null;

    // Reorder mode (staff-assisted) — customer wants the same design as a
    // past order, no new artwork to upload/position. See syncFormProperties
    // for the gate short-circuit and bindReorderToggle for the UI wiring.
    var reorderMode = false;
    var reorderOrderNumber = '';
    var reorderDetails = '';

    // Customer-initiated flag, not a gate — see syncFormProperties, where
    // it's never part of `blocked`. Distinct from the low-DPI advisory
    // (which fires automatically off the upload's own resolution): this is
    // for a customer who isn't sure about the auto-cleaned preview for any
    // reason a resolution check can't catch (background removal ate part
    // of the design, colours look off, etc.) and wants a human to actually
    // look before it goes to press.
    var designReviewRequested = false;

    initColourSync();
    Object.keys(productCfg.sides).forEach(buildZoneButtons);
    buildLocationChoices();
    bindUploads();
    bindMaxSizeButton();
    bindWizardNav();
    bindReorderToggle();
    bindAiGenerateToggles();
    simplifyPrintLocationSelector();

    var emptyStateEl = root.querySelector('[data-empty-state]');

    function showEmptyState() {
      if (emptyStateEl) emptyStateEl.hidden = false;
      root.querySelectorAll('[data-wizard-step]').forEach(function (el) { el.hidden = true; });
    }

    function hideEmptyState() {
      if (emptyStateEl) emptyStateEl.hidden = true;
    }

    if (orderBuilderMode && wizard.chosenSides.length === 0) {
      showEmptyState();
    } else {
      hideEmptyState();
      goToWizardStep(0);
    }
    syncFormProperties();

    // Live reactivity: ft-order-builder's print-location pills toggle the
    // real pls-* checkboxes, which dispatch a real `change` event on
    // themselves (see print-location-selector.liquid's own listener) — so
    // this fires for every toggle regardless of whether the click came
    // from a pill or (in principle) the checkbox itself. Recomputes which
    // sides are chosen and rebuilds wizard.steps every time, same
    // add-a-design-step / clear-art-and-remove-it pattern
    // finalizeSidesChoice() already uses for the old yes/no flow — just
    // triggered externally instead of by those buttons. Only wired up in
    // orderBuilderMode; every other product's PLS checkboxes keep being
    // driven one-way (wizard -> checkboxes) exactly as before.
    if (orderBuilderMode) {
      Object.keys(PLS_ZONE_MAP).forEach(function (sideId) {
        var zoneMap = PLS_ZONE_MAP[sideId];
        Object.keys(zoneMap).forEach(function (zoneId) {
          var checkbox = document.getElementById(zoneMap[zoneId]);
          if (!checkbox) return;
          checkbox.addEventListener('change', function () {
            var newChosen = [];
            sideIds.forEach(function (id) {
              var selectedZoneId = preSelectedZoneForSide(id);
              if (selectedZoneId) {
                newChosen.push(id);
                state.sides[id].zoneId = selectedZoneId;
              }
              // The zone badge built by buildZoneButtons() is static in
              // orderBuilderMode (nothing to click), so it's only ever
              // correct if rebuilt whenever the underlying zoneId changes.
              // It was built once at page init, before any pill had been
              // touched — state.sides[id].zoneId at that point is just each
              // side's generic default (prefers the "full_" zone), not
              // whatever the customer actually goes on to pick. Without
              // this, the badge stays frozen on that initial default
              // forever, showing e.g. "Large Front" even after the
              // customer picked "Front Left Chest".
              buildZoneButtons(id);
            });

            wizard.chosenSides.forEach(function (id) {
              if (newChosen.indexOf(id) === -1 && state.sides[id].art) clearArt(id);
            });

            wizard.chosenSides = newChosen;
            wizard.pendingSides = newChosen.slice();
            wizard.steps = computeWizardSteps(newChosen);

            if (newChosen.length === 0) {
              showEmptyState();
              notifyWizardChange(); // goToWizardStep (which also notifies) isn't called on this branch
            } else {
              hideEmptyState();
              goToWizardStep(0);
            }
            syncFormProperties();
          });
        });
      });

      // Merged-wizard hand-off surface for the ft-order-wizard orchestrator
      // (assets/ft-order-wizard.js) — only assigned in orderBuilderMode, so
      // every other mockup-ready product is unaffected. This widget's own
      // progress bar and Back/Next nav are hidden here too since the
      // orchestrator's are now the only ones visible on the page — same
      // inline-style-over-[hidden] reasoning as updateWizardNav's own
      // comment above (author-origin `button{display:inline-block}` beats
      // the [hidden] user-agent rule).
      var ownProgressEl = root.querySelector('[data-wizard-progress]');
      if (ownProgressEl) ownProgressEl.style.display = 'none';
      var ownNavEl = root.querySelector('.ft-mb__nav');
      if (ownNavEl) ownNavEl.style.display = 'none';

      root.__ftMockupController = {
        reenter: reenter,
        goNext: wizardGoNext,
        goBack: wizardGoBack,
        getStepCount: function () { return wizard.steps.length; },
        getCurrentIndex: function () { return wizard.currentIndex; },
        getNavState: getNavState,
        getStepLabel: getStepLabel,
        getStepSideLabel: getStepSideLabel,
        onWizardChange: function (cb) { wizardChangeListeners.push(cb); },
        // Read-only — lets ft-order-builder.js show "design attached" on
        // a pill without reaching into this widget's internal state
        // directly (2026-09-04, usability audit: picking a 2nd print
        // location gave no visual sign the 1st already had a design
        // while the 2nd still needed one).
        hasArt: function (sideId) {
          var s = state.sides[sideId];
          return !!(s && s.art && s.art.url);
        }
      };

      var headEl = root.querySelector('.ft-mb__head');
      if (headEl) headEl.style.display = 'none'; // ft-order-wizard's own intro replaces this
    }

    /* ========================================================
     * UI builders
     * ======================================================== */

    // Colour is driven entirely by whatever REAL colour control the
    // customer actually sees and clicks — NOT a second, independent picker
    // inside the mockup builder. Two separate colour controls on one page
    // risk disagreeing (customer picks Pink for real but the mockup still
    // shows whatever it defaulted to) — matching the real selector is the
    // only way the preview, the background-removal knockout (which assumes
    // black based on state.colour), and the `Preview Colour` order property
    // can't drift from what was actually ordered.
    //
    // Real gap found 2026-08-02: for Printable products, snippets/
    // size-quantity-matrix.liquid renders its OWN colour swatches
    // (`.size-qty-matrix__swatch`, its own `selectedColor` JS variable) and
    // the theme hides the plain native `input[name="Color"]` radios this
    // code originally listened to entirely (0×0, confirmed via the live
    // DOM — not just visually hidden, genuinely never interacted with).
    // size-quantity-matrix's own selectColor() never touches those native
    // radios either, so listening to them silently synced to an input
    // real customers never touch. Delegated click listener on the ACTUAL
    // swatch buttons instead — delegation (not a direct listener attached
    // once at init) because size-quantity-matrix's swatches may not exist
    // in the DOM yet at the exact moment this script runs, and a direct
    // listener on elements that get rebuilt later would go stale the same
    // way. Kept the original native-radio path too, as a fallback for any
    // OTHER Printable product that doesn't route through size-quantity-
    // matrix's swatch UI (e.g. color_swatches block setting switched on,
    // rendering snippets/variant-button.liquid's data-color-name markup).
    function initColourSync() {
      // Nothing to sync — showing a preview toggle with only one possible
      // colour isn't a real decision either way.
      if (!productCfg.colours || productCfg.colours.length <= 1) return;

      function colourIdForLabel(label) {
        var lower = (label || '').toLowerCase();
        var match = productCfg.colours.filter(function (c) { return c.label.toLowerCase() === lower; })[0];
        return match ? match.id : null;
      }

      function applyColour(id) {
        if (!id || id === state.colour) return;
        state.colour = id;
        updateLocationChoiceThumbs();
        updateNativeProductImage(id);
        renderSide().then(syncFormProperties);
      }

      // Swaps the real Shopify product gallery's main photo to match the
      // selected colour. Doesn't hook into the theme's native "image sets"
      // carousel feature (data-group/Flickity) — traced that mechanism and
      // it depends on several fragile, undocumented pieces (a specific
      // wrapper markup, a namespaced custom-event chain, a full slider
      // re-init) that didn't actually swap the visible slide in testing,
      // and there's no evidence any product on this store ever exercised
      // it successfully before. Directly setting the visible image's src
      // is simple, reliable, and reuses the same per-colour front-image
      // URLs already used for the mockup builder's own thumbnails.
      function updateNativeProductImage(id) {
        var frontCfg = productCfg.sides && productCfg.sides.front;
        if (!frontCfg) return;
        var url = frontCfg.templates[id] || frontCfg.templates._default;
        if (!url) return;
        // The gallery is a Flickity slideshow ([data-product-photos], one
        // .product-main-slide per product photo) — Flickity marks whichever
        // slide the customer is actually looking at with `is-selected`.
        // Plain `[data-product-photos] img` always grabs the FIRST slide in
        // DOM order, which stopped being the visible one the moment this
        // product's real variant images were linked (that made a LATER
        // media item — not the first — the featured/starting slide).
        // Editing the wrong slide meant this silently updated an invisible
        // image on every colour change; customers never saw it move.
        var mainImg = doc.querySelector('[data-product-photos] .product-main-slide.is-selected img')
          || doc.querySelector('[data-product-photos] .product-main-slide img')
          || doc.querySelector('[data-product-photos] img');
        if (!mainImg) return;
        mainImg.src = url;
        mainImg.removeAttribute('srcset');
        if ('src' in mainImg.dataset) mainImg.dataset.src = url;
        if ('srcset' in mainImg.dataset) delete mainImg.dataset.srcset;
      }

      var doc = root.ownerDocument;

      // ---- Primary: size-quantity-matrix.liquid's own swatch buttons ----
      var initialSwatch = doc.querySelector('.size-qty-matrix__swatch.is-selected')
        || doc.querySelector('.size-qty-matrix__swatch');
      if (initialSwatch) {
        var initialSwatchId = colourIdForLabel(initialSwatch.getAttribute('data-color'));
        if (initialSwatchId) state.colour = initialSwatchId;
      }
      doc.addEventListener('click', function (e) {
        var btn = e.target.closest && e.target.closest('.size-qty-matrix__swatch');
        if (!btn) return;
        // Fires on click, ahead of size-quantity-matrix's own handler
        // finishing its DOM work — that's fine, applyColour only reads
        // data-color off the clicked button itself, not any state
        // size-quantity-matrix hasn't set yet.
        applyColour(colourIdForLabel(btn.getAttribute('data-color')));
      });

      // ---- Fallback: plain native radio, name="Color"/"Colour" ----
      var colourInputs = Array.prototype.filter.call(
        doc.querySelectorAll('input[type="radio"][name]'),
        function (input) {
          var name = (input.name || '').toLowerCase();
          return name === 'color' || name === 'colour';
        }
      );
      if (!colourInputs.length) return;

      if (!initialSwatch) {
        var checked = colourInputs.filter(function (i) { return i.checked; })[0];
        if (checked) {
          var initialId = colourIdForLabel(checked.value);
          if (initialId) state.colour = initialId;
        }
      }

      colourInputs.forEach(function (input) {
        input.addEventListener('change', function () {
          if (!input.checked) return;
          applyColour(colourIdForLabel(input.value));
        });
      });
    }

    // Companion to the data-choice-thumb-side tagging in buildZoneVisualBlock
    // — re-points each location-choice thumbnail at the new colour's
    // template whenever state.colour changes after init.
    function updateLocationChoiceThumbs() {
      root.querySelectorAll('img[data-choice-thumb-side]').forEach(function (img) {
        var sideId = img.getAttribute('data-choice-thumb-side');
        var sideCfg = productCfg.sides[sideId];
        if (!sideCfg) return;
        img.src = sideCfg.templates[state.colour] || sideCfg.templates._default;
      });
    }

    // "Max size" sits next to the canvas (shared across sides, like the
    // size chip) rather than per-side, so it always acts on whichever
    // side is currently showing.
    function bindMaxSizeButton() {
      var btn = root.querySelector('[data-max-size]');
      if (!btn) return;
      btn.addEventListener('click', function () {
        maxOutArt(state.activeSide);
      });
    }

    function buildZoneButtons(sideId) {
      var row = root.querySelector('[data-zone-row="' + sideId + '"]');
      if (!row) return;

      var zones = productCfg.sides[sideId].zones;
      var zoneIds = Object.keys(zones);

      // In orderBuilderMode the print-location pills in the combined
      // shirts + print-locations step already pick exactly one zone per
      // side (preSelectedZoneForSide, read from whichever pill's checkbox
      // is checked) before the customer ever reaches this design step.
      // Showing the full interactive switcher here would let them silently
      // swap to a zone they never actually selected \u2014 e.g. picking "Front
      // Left Chest" but then switching to "Large Front" here, printing in
      // a location they were never charged the right fee for. But the
      // size info itself is still worth showing up front, before any
      // artwork is uploaded (updateSizeChip only appears once art
      // exists) \u2014 so render the single pre-selected zone as a static,
      // non-clickable badge instead of hiding it outright.
      if (orderBuilderMode) {
        var lockedZoneId = state.sides[sideId] && state.sides[sideId].zoneId;
        var lockedZone = lockedZoneId && zones[lockedZoneId];
        if (!lockedZone) { row.hidden = true; return; }
        row.hidden = false;
        row.innerHTML = '';
        var badge = document.createElement('div');
        badge.className = 'ft-mb__zone-btn is-active is-static';
        badge.innerHTML = '<span>' + lockedZone.label + '</span><small>' + lockedZone.print_mm.width + '\u00d7' + lockedZone.print_mm.height + 'mm max</small>';
        row.appendChild(badge);
        return;
      }

      // Only one option \u2014 nothing to choose, so don't show a picker for it.
      if (zoneIds.length <= 1) { row.hidden = true; return; }

      zoneIds.forEach(function (zoneId) {
        var z = zones[zoneId];
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'ft-mb__zone-btn' + (state.sides[sideId].zoneId === zoneId ? ' is-active' : '');
        b.dataset.zone = zoneId;
        b.innerHTML = '<span>' + z.label + '</span><small>' + z.print_mm.width + '\u00d7' + z.print_mm.height + 'mm max</small>';
        b.addEventListener('click', function () {
          state.sides[sideId].zoneId = zoneId;
          row.querySelectorAll('.ft-mb__zone-btn').forEach(function (s) { s.classList.remove('is-active'); });
          b.classList.add('is-active');
          recomputeSide(sideId);
          if (state.activeSide === sideId) renderSide();
          syncFormProperties();
        });
        row.appendChild(b);
      });
    }

    // Garment thumbnail + dashed outline showing exactly where and how big
    // the print will be, so the sides-choice cards aren't just blind text
    // buttons \u2014 the customer sees the actual placement before committing
    // to a side. One block per zone a side actually offers (e.g. front has
    // both Left Chest and Large Front) \u2014 showing only the default zone
    // hid the fact there was a choice at all.
    function buildZoneVisualBlock(sideId, zoneId) {
      var sideCfg = productCfg.sides[sideId];
      var z = sideCfg.zones[zoneId];
      var tplUrl = sideCfg.templates[state.colour] || sideCfg.templates._default;

      var block = document.createElement('div');
      block.className = 'ft-mb__choice-block';

      var thumb = document.createElement('div');
      thumb.className = 'ft-mb__choice-thumb';
      var img = document.createElement('img');
      img.src = tplUrl;
      img.alt = sideCfg.label + ' template';
      // Built once at init with whatever state.colour is at that moment —
      // tagged so a later colour change (initColourSync's change handler)
      // can find and update it. Without this these thumbnails silently keep
      // showing the colour the page happened to load with forever, even
      // though the real per-zone canvas further into the wizard updates
      // correctly — this is the first design visual a customer sees, so a
      // stale one here reads as "the colour picker doesn't do anything."
      img.setAttribute('data-choice-thumb-side', sideId);
      thumb.appendChild(img);

      var box = document.createElement('div');
      box.className = 'ft-mb__choice-zone';
      box.style.left = (z.rect.x / canvasW * 100) + '%';
      box.style.top = (z.rect.y / canvasH * 100) + '%';
      box.style.width = (z.rect.width / canvasW * 100) + '%';
      box.style.height = (z.rect.height / canvasH * 100) + '%';
      thumb.appendChild(box);
      block.appendChild(thumb);

      // Size lives in the caption below, not inside the box itself \u2014 a
      // small zone like Left Chest is barely bigger than the label text,
      // so putting it inside just covered the garment it's meant to show.
      var caption = document.createElement('span');
      caption.className = 'ft-mb__choice-caption';
      caption.textContent = z.label + ' \u2014 ' + z.print_mm.width + '\u00d7' + z.print_mm.height + 'mm';
      block.appendChild(caption);

      return block;
    }

    // Step 1: one full-screen panel per print location in the config
    // (Front, Back, and whatever gets added later — Left Sleeve, Right
    // Sleeve, etc.), each with its own yes/no decision instead of one
    // screen combining every location behind toggle cards. Scales to any
    // number of locations with no code changes — new locations just need
    // an entry in ft-print-zones.json.
    function buildLocationChoices() {
      var grid = root.querySelector('[data-location-grid]');
      if (!grid) return;

      sideIds.forEach(function (sideId) {
        var sideCfg = productCfg.sides[sideId];

        var panel = document.createElement('div');
        panel.className = 'ft-mb__location-panel';
        panel.dataset.locationPanel = sideId;
        panel.hidden = true;

        var visual = document.createElement('div');
        visual.className = 'ft-mb__choice-visual';
        Object.keys(sideCfg.zones).forEach(function (zoneId) {
          visual.appendChild(buildZoneVisualBlock(sideId, zoneId));
        });
        panel.appendChild(visual);

        var actions = document.createElement('div');
        actions.className = 'ft-mb__location-actions';

        var yesBtn = document.createElement('button');
        yesBtn.type = 'button';
        yesBtn.className = 'ft-mb__location-yes';
        yesBtn.textContent = 'Yes, print on ' + sideCfg.label.toLowerCase();
        yesBtn.addEventListener('click', function () { chooseSide(sideId, true); });
        actions.appendChild(yesBtn);

        var noBtn = document.createElement('button');
        noBtn.type = 'button';
        noBtn.className = 'ft-mb__location-no';
        noBtn.textContent = 'No thanks';
        noBtn.addEventListener('click', function () { chooseSide(sideId, false); });
        actions.appendChild(noBtn);

        panel.appendChild(actions);
        grid.appendChild(panel);
      });
    }

    // A side's yes/no answer both records the choice and is the navigation
    // — no separate "confirm" press needed. Reaching the last side's
    // screen locks in the full chosen-sides list and appends the
    // design/review steps that follow from it.
    function chooseSide(sideId, wantsPrint) {
      var idx = wizard.pendingSides.indexOf(sideId);
      if (wantsPrint && idx === -1) wizard.pendingSides.push(sideId);
      if (!wantsPrint && idx !== -1) wizard.pendingSides.splice(idx, 1);

      if (wizard.currentIndex === choiceSteps.length - 1) finalizeSidesChoice();
      goToWizardStep(wizard.currentIndex + 1);
    }

    function showLocationPanel(sideId) {
      root.querySelectorAll('[data-location-panel]').forEach(function (p) {
        p.hidden = p.dataset.locationPanel !== sideId;
      });
    }

    // Shows one side's controls panel (and hides the other) \u2014 reused by
    // the wizard when entering a 'design:<side>' step. No more tabs; the
    // wizard itself is the only way to move between sides now.
    function showSidePanel(sideId) {
      sidePanels.forEach(function (p) {
        var isActive = p.dataset.sidePanel === sideId;
        p.hidden = !isActive;
        p.classList.toggle('is-front-of-mind', isActive);
      });

      // Single checkbox, relocated into whichever side's upload block is
      // now active \u2014 keeps it physically at the uploads only, never next
      // to the preview/canvas or any other side's controls.
      var uploadBlock = root.querySelector('[data-upload-block="' + sideId + '"]');
      var confirmWrap = root.querySelector('[data-copyright-confirm-wrap]');
      if (uploadBlock && confirmWrap) uploadBlock.appendChild(confirmWrap);

      // Defined in bindAiGenerateToggles(), called after — later functions
      // in this same init pass, so guard rather than assume load order.
      if (root.__ftApplyPendingAiDesign) root.__ftApplyPendingAiDesign(sideId);
      if (root.__ftRefreshAiToggleCopy) root.__ftRefreshAiToggleCopy();
    }

    /* ========================================================
     * Upload flow
     * ======================================================== */

    function bindUploads() {
      Object.keys(productCfg.sides).forEach(function (sideId) {
        var input = root.querySelector('[data-file-input="' + sideId + '"]');
        var removeBtn = root.querySelector('[data-remove-art="' + sideId + '"]');
        var undoTrimBtn = root.querySelector('[data-undo-trim="' + sideId + '"]');
        if (!input) return;

        input.addEventListener('change', function () {
          var file = input.files && input.files[0];
          if (file) handleFile(sideId, file);
          input.value = '';
        });

        if (removeBtn) {
          removeBtn.addEventListener('click', function () { clearArt(sideId); });
        }

        if (undoTrimBtn) {
          undoTrimBtn.addEventListener('click', function () {
            var s = state.sides[sideId];
            if (!s.art || !s.art.trimmed) return;
            applyArt(sideId, s.art.originalFile, s.art.originalImage, false);
          });
        }
      });
    }

    // Also used by the wizard when a side gets deselected on the "sides"
    // step after previously having artwork — without this, going back and
    // switching from "Both" to "Front only" would leave the back design
    // (and its price) silently attached even though it's no longer shown.
    function clearArt(sideId) {
      state.sides[sideId].art = null;
      state.sides[sideId].image = null;
      state.sides[sideId].dpi = null;
      state.sides[sideId].printMm = null;
      state.sides[sideId].placement = null;
      updateUploadMeta(sideId);
      updateDpiBadge(sideId);
      if (state.activeSide === sideId) renderSide();
      syncFormProperties();
      if (root.__ftRefreshAiToggleCopy) root.__ftRefreshAiToggleCopy();
    }

    async function handleFile(sideId, file) {
      var metaEl = root.querySelector('[data-upload-meta="' + sideId + '"]');
      var maxBytes = (config.upload && config.upload.max_bytes) || 52428800;
      if (file.size > maxBytes) {
        metaEl.innerHTML = '<span class="ft-mb__err">File is over ' + Math.round(maxBytes / 1048576) + 'MB. Save a smaller version and try again.</span>';
        return;
      }

      metaEl.innerHTML = '<span class="ft-mb__spinner" aria-hidden="true"></span>Reading artwork\u2026';

      // Decode locally first \u2014 instant preview, and we get natural px for DPI
      var objectUrl = URL.createObjectURL(file);
      var img;
      try {
        img = await loadImage(objectUrl);
      } catch (e) {
        metaEl.innerHTML = '<span class="ft-mb__err">Couldn\u2019t read that file. PNG with a transparent background works best.</span>';
        return;
      }

      await applyArt(sideId, file, img, true);
    }

    // Places (and uploads) a decoded image for a side. When `allowTrim` is
    // true, auto-detects and crops away surrounding transparent/flat-colour
    // padding first, so the DPI check and print size reflect the real
    // design, not the whole uploaded canvas. Re-invoked with allowTrim=false
    // (and the original file/image) from the "use untrimmed file" control.
    async function applyArt(sideId, file, img, allowTrim) {
      var originalW = img.naturalWidth || img.width;
      var originalH = img.naturalHeight || img.height;
      var useImage = img;
      var useW = originalW;
      var useH = originalH;
      var trimmed = false;
      var bgRemoved = false;
      var cropped = false;
      var uploadSource = file;

      if (allowTrim) {
        // Background (and, on black garments, black) removal runs first so
        // the crop-to-content step below sees real transparency and tightens
        // around it automatically — same alpha-based path it already used
        // for PNGs with a transparent background. Black knockout only makes
        // sense on black garments (other colours need their black kept —
        // it prints fine there), and only on large prints — a small logo
        // (e.g. Left Chest) relies on black for outlines/detail at that
        // size, and knocking it out leaves nothing printable.
        var isLargeZone = state.sides[sideId].zoneId.indexOf('full_') === 0;
        var knockOutBlack = state.colour === 'black' && isLargeZone;
        var strippedCanvas = removeBackground(img, knockOutBlack);
        bgRemoved = !!strippedCanvas;
        var stripped = strippedCanvas || img;
        var bbox = detectContentBBox(stripped);
        cropped = !!bbox;
        var cleaned = bbox ? cropToCanvas(bbox) : (bgRemoved ? stripped : null);
        if (cleaned) {
          useImage = cleaned;
          useW = cleaned.width;
          useH = cleaned.height;
          trimmed = true;
          uploadSource = await new Promise(function (resolve) {
            cleaned.toBlob(function (blob) {
              resolve(new File([blob], trimmedFilename(file.name), { type: 'image/png' }));
            }, 'image/png');
          });
        }
      }

      state.sides[sideId].art = {
        url: null, // filled after upload
        key: null,
        filename: file.name,
        naturalW: useW,
        naturalH: useH,
        trimmed: trimmed,
        bgRemoved: bgRemoved,
        cropped: cropped,
        originalW: originalW,
        originalH: originalH,
        originalFile: file,
        originalImage: img,
        // Checked against the FINAL file (post crop/removal), not just
        // whether removeBackground fired — see hasAdequateTransparency's
        // own comment for why. Runs regardless of allowTrim, since staff
        // need to know if the file about to be printed has this problem
        // even when the customer chose "use original, no clean-up".
        bgLikelyNotRemoved: !hasAdequateTransparency(useImage)
      };
      state.sides[sideId].image = useImage;
      recomputeSide(sideId);
      updateUploadMeta(sideId, 'Uploading\u2026');
      updateDpiBadge(sideId);
      // Uploads only happen while that side's design step is showing, so
      // activeSide already matches \u2014 no side-switch needed here anymore.
      await renderSide();

      // Upload to R2 in the background (the trimmed version, if we trimmed)
      try {
        var uploaded = await uploadFile(uploadEndpoint, uploadSource);
        state.sides[sideId].art.url = uploaded.url;
        state.sides[sideId].art.key = uploaded.key || null;
        updateUploadMeta(sideId);

        // Also keep the customer's original, unedited file reachable —
        // previously ONLY the auto-trimmed/background-removed version ever
        // reached storage, so once the browser session ended there was no
        // way back to what the customer actually sent, even if the
        // automatic clean-up needed to be redone differently for a
        // specific job. Confirmed missing on a real order, 2026-08-17.
        // Best-effort: if this second upload fails, the main (print-ready)
        // upload above is already done and unaffected — never block on this.
        if (trimmed) {
          try {
            var uploadedOriginal = await uploadFile(uploadEndpoint, file);
            state.sides[sideId].art.originalUrl = uploadedOriginal.url;
          } catch (origErr) {
            console.error('[ft-mb] original-file backup upload failed:', origErr);
            state.sides[sideId].art.originalUrl = null;
          }
        } else {
          // Nothing was trimmed — uploadSource already IS the original file.
          state.sides[sideId].art.originalUrl = uploaded.url;
        }

        syncFormProperties();
        if (root.__ftRefreshAiToggleCopy) root.__ftRefreshAiToggleCopy();
      } catch (e) {
        console.error('[ft-mb] upload failed:', e);
        // If the worker responded (e.status set), show its reason; otherwise
        // it's a network/connection failure.
        var msg = (e && e.status) ? e.message : 'Upload failed \u2014 check your connection and try again.';
        updateUploadMeta(sideId, null, msg);
        state.sides[sideId].art.url = null;
        syncFormProperties();
      }
    }

    function trimmedFilename(name) {
      return name.replace(/(\.[a-z0-9]+)?$/i, function (ext) { return '-trimmed' + (ext || '.png'); });
    }

    // R2 worker contract (confirmed against the live endpoint):
    //   POST multipart, field "file"
    //   200 -> { "url": "https://pub-<hash>.r2.dev/customer-uploads/YYYY-MM-DD/<uuid>-<name>", "name": "...", "size": 123 }
    //   4xx -> { "error": "human-readable message" } (e.g. 415 unsupported type, 400 no file)
    // The worker does not return an R2 object key; we derive it from the URL path.
    async function uploadFile(endpoint, file) {
      var fd = new FormData();
      fd.append('file', file, file.name);

      var res = await fetch(endpoint, { method: 'POST', body: fd });

      var data = null;
      try { data = await res.json(); } catch (e) { /* non-JSON body (proxy/edge error) */ }

      if (!res.ok) {
        var msg = (data && data.error) || ('Upload failed (HTTP ' + res.status + ')');
        var err = new Error(msg);
        err.status = res.status; // marks this as a server response, not a network drop
        throw err;
      }

      var url = data && data.url;
      if (!url) throw new Error('Upload response missing url');

      // Derive the R2 object key from the public URL path (everything after
      // the bucket domain), e.g. "customer-uploads/2026-07-14/<uuid>-name.png".
      // The renderer can fetch by url directly; key is a convenience for R2 ops.
      var key = null;
      try { key = decodeURIComponent(new URL(url).pathname.replace(/^\/+/, '')); }
      catch (e) { /* leave null if the url can't be parsed */ }

      return { url: url, key: key };
    }

    function updateUploadMeta(sideId, override, error) {
      var metaEl = root.querySelector('[data-upload-meta="' + sideId + '"]');
      var noteEl = root.querySelector('[data-adjust-note="' + sideId + '"]');
      var removeBtn = root.querySelector('[data-remove-art="' + sideId + '"]');
      var undoTrimBtn = root.querySelector('[data-undo-trim="' + sideId + '"]');
      var label = root.querySelector('[data-upload-label="' + sideId + '"]');
      var s = state.sides[sideId];
      var metaSideCfg = productCfg.sides[sideId];
      var metaSideLabel = metaSideCfg && metaSideCfg.label ? metaSideCfg.label.toLowerCase() : sideId;

      if (!s.art) {
        metaEl.textContent = '';
        if (noteEl) { noteEl.hidden = true; noteEl.innerHTML = ''; }
        if (removeBtn) removeBtn.hidden = true;
        if (undoTrimBtn) undoTrimBtn.hidden = true;
        if (label) label.textContent = 'Upload ' + metaSideLabel + ' artwork';
        return;
      }

      // Buttons reflect the current art/trim state regardless of whether
      // we're showing an error or a status override below \u2014 an upload
      // failure shouldn't leave the customer unable to remove/undo.
      if (removeBtn) removeBtn.hidden = false;
      if (undoTrimBtn) undoTrimBtn.hidden = !s.art.trimmed;
      if (label) label.textContent = 'Replace ' + metaSideLabel + ' artwork';

      if (error) { metaEl.innerHTML = '<span class="ft-mb__err">' + error + '</span>'; if (noteEl) noteEl.hidden = true; return; }
      if (override) {
        // "…" flags this as an in-progress state, not a final result — pairs
        // it with a spinner so a customer glancing at the upload area mid-
        // upload sees something visibly moving, not just static text.
        var isLoading = override.indexOf('…') !== -1;
        metaEl.innerHTML = isLoading ? '<span class="ft-mb__spinner" aria-hidden="true"></span>' + override : override;
        if (noteEl) noteEl.hidden = true;
        return;
      }

      var status = s.art.url ? 'uploaded' : 'preview only';
      metaEl.textContent = s.art.filename + ' \u2014 ' + s.art.naturalW + '\u00d7' + s.art.naturalH + 'px (' + status + ')';

      // Spelled out plainly, not buried in the filename line, so a customer
      // notices their file was changed before they check out \u2014 paired
      // with the always-visible Revert button right below it.
      if (noteEl) {
        if (s.art.trimmed) {
          var actions = [];
          if (s.art.bgRemoved) actions.push('removed the background');
          if (s.art.cropped) actions.push('cropped it in tight to your design');
          noteEl.innerHTML = '<strong>We\u2019ve adjusted this image:</strong> ' + actions.join(' and ') + '. ' +
            'Original: ' + s.art.originalW + '\u00d7' + s.art.originalH + 'px \u2192 now ' +
            s.art.naturalW + '\u00d7' + s.art.naturalH + 'px. Not right? Use Revert below.';
          noteEl.hidden = false;
        } else {
          noteEl.hidden = true;
          noteEl.innerHTML = '';
        }
      }
    }

    /* ========================================================
     * Wizard navigation
     * ======================================================== */

    function computeWizardSteps(chosenSides) {
      var steps = choiceSteps.slice();
      chosenSides.forEach(function (sideId) { steps.push('design:' + sideId); });
      steps.push('review');
      return steps;
    }

    function isWizardStepReady(stepId) {
      if (stepId.indexOf('design:') === 0) {
        var sideId = stepId.split(':')[1];
        var s = state.sides[sideId];
        // Low DPI is advisory, not a hard stop — a low-res upload still
        // gets reviewed (and can be touched up) by a designer before
        // printing, so blocking the purchase here just loses the sale.
        return !!(s.art && s.art.url);
      }
      return true;
    }

    // Extracted from the Back/Next click handlers (rather than left
    // inline) so both the real buttons AND, in orderBuilderMode, the
    // ft-order-wizard orchestrator's own shared buttons can call the
    // exact same gating logic — one copy, not two that can drift.
    // wizardGoBack() returns false when already at index 0 instead of
    // just no-op'ing, so a caller (the orchestrator) knows to hand
    // control back to whatever comes before this wizard.
    function wizardGoNext() {
      if (wizard.currentIndex < wizard.steps.length - 1) {
        goToWizardStep(wizard.currentIndex + 1);
        return true;
      }
      return false;
    }

    function wizardGoBack() {
      if (wizard.currentIndex > 0) {
        goToWizardStep(wizard.currentIndex - 1);
        return true;
      }
      return false;
    }

    function bindWizardNav() {
      var backBtn = root.querySelector('[data-wizard-back]');
      var nextBtn = root.querySelector('[data-wizard-next]');

      backBtn.addEventListener('click', wizardGoBack);
      nextBtn.addEventListener('click', wizardGoNext);

      var reviewCta = root.querySelector('[data-review-cta]');
      reviewCta.addEventListener('click', function () {
        // Proxies the click to whichever real add-to-cart control the
        // theme renders (size-quantity-matrix's own button, or a plain
        // submit) \u2014 reuses its existing enabled/disabled gating rather
        // than re-implementing the cart-add itself.
        var form = findProductForm();
        var realBtn = document.getElementById('pls-main-add-btn') ||
          (form && form.querySelector('[type="submit"], button[name="add"]'));
        if (realBtn) realBtn.click();
      });

      var copyrightCheckbox = root.querySelector('[data-copyright-confirm]');
      copyrightCheckbox.addEventListener('change', function () {
        copyrightConfirmed = copyrightCheckbox.checked;
        if (copyrightConfirmed && !copyrightConfirmedAt) copyrightConfirmedAt = new Date().toISOString();
        if (!copyrightConfirmed) copyrightConfirmedAt = null;
        syncFormProperties();
      });

      var reviewRequestCheckbox = root.querySelector('[data-review-request]');
      if (reviewRequestCheckbox) {
        reviewRequestCheckbox.addEventListener('change', function () {
          designReviewRequested = reviewRequestCheckbox.checked;
          syncFormProperties();
        });
      }
    }

    // Reorder toggle lives outside the step wizard entirely (always visible,
    // right under the widget heading) rather than as another wizard step —
    // it's a shortcut PAST the whole sides/design flow, not a step within
    // it. Toggling it just swaps which chunk of markup is visible; none of
    // the wizard's own step/side-selection state is touched, so switching
    // back off lands the customer exactly where goToWizardStep(currentIndex)
    // would put them normally.
    // Fix, 2026-09-03 (urgent — confirmed real orders shipped with print
    // costs missing): reorder mode has always skipped the wizard's own
    // "Would you like a print on X?" screens entirely — those are the ONLY
    // thing that normally checks the real print-location-selector.liquid
    // checkboxes size-quantity-matrix.liquid reads to decide which print-
    // location fee line items to add. With nothing else in the reorder
    // panel ever touching those checkboxes, a reorder could go all the way
    // to checkout with every location unchecked — correct-looking order,
    // silently missing every print charge. Builds a plain checklist from
    // the same real .pls-option elements ft-order-builder.js's pills
    // already read (present on every Printable product, independent of
    // AI-mockup zone config), each one directly driving its matching real
    // checkbox — same direct-toggle pattern the pills use, so this is
    // exactly as authoritative as picking locations via the wizard itself.
    function buildReorderLocationChecklist() {
      var container = root.querySelector('[data-reorder-locations]');
      if (!container || container.childElementCount) return; // build once
      var options = document.querySelectorAll('.pls-option[data-location][data-price]');
      options.forEach(function (opt) {
        var realCheckbox = opt.querySelector('input[type="checkbox"]');
        if (!realCheckbox) return;
        var location = opt.getAttribute('data-location');
        var titleEl = opt.querySelector('.pls-option-title') || opt.querySelector('.pls-promo-locked-text strong');
        var label = titleEl ? titleEl.textContent.trim() : location;
        var price = parseFloat(opt.getAttribute('data-price')) || 0;

        var wrap = document.createElement('label');
        wrap.className = 'ft-mb__reorder-location';
        var cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.checked = realCheckbox.checked;
        cb.addEventListener('change', function () {
          realCheckbox.checked = cb.checked;
          realCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
          syncFormProperties();
        });
        wrap.appendChild(cb);
        var span = document.createElement('span');
        span.textContent = price ? (label + ' (+$' + price.toFixed(2) + ')') : label;
        wrap.appendChild(span);
        container.appendChild(wrap);
      });
    }

    function anyReorderLocationChecked() {
      var boxes = document.querySelectorAll('.pls-option[data-location] input[type="checkbox"]');
      for (var i = 0; i < boxes.length; i++) {
        if (boxes[i].checked) return true;
      }
      return false;
    }

    function bindReorderToggle() {
      var toggle = root.querySelector('[data-reorder-toggle]');
      if (!toggle) return; // older cached markup — feature simply absent, not broken
      var panel = root.querySelector('[data-reorder-panel]');
      var numberInput = root.querySelector('[data-reorder-order-number]');
      var detailsInput = root.querySelector('[data-reorder-details]');
      var reorderCta = root.querySelector('[data-reorder-cta]');
      var navEl = root.querySelector('.ft-mb__nav');

      var progressEl = root.querySelector('.ft-mb__progress');

      toggle.addEventListener('change', function () {
        reorderMode = toggle.checked;
        if (reorderMode) {
          if (panel) panel.hidden = false;
          buildReorderLocationChecklist();
          root.querySelectorAll('[data-wizard-step]').forEach(function (el) { el.hidden = true; });
          if (progressEl) progressEl.hidden = true;
          if (navEl) navEl.hidden = true;
          if (emptyStateEl) emptyStateEl.hidden = true;
        } else {
          if (panel) panel.hidden = true;
          if (progressEl) progressEl.hidden = false;
          if (navEl) navEl.hidden = false;
          // Restores real step visibility (and nav enabled state) exactly
          // as init does — goToWizardStep only unhides the ONE current
          // step, so every [data-wizard-step] must start hidden again
          // first, otherwise the previously-forced-visible ones all stay
          // visible at once alongside it.
          root.querySelectorAll('[data-wizard-step]').forEach(function (el) { el.hidden = true; });
          if (orderBuilderMode && wizard.chosenSides.length === 0) showEmptyState();
          else { hideEmptyState(); goToWizardStep(wizard.currentIndex); }
        }
        syncFormProperties();
      });

      if (numberInput) {
        numberInput.addEventListener('input', function () {
          reorderOrderNumber = numberInput.value;
          syncFormProperties();
        });
      }
      if (detailsInput) {
        detailsInput.addEventListener('input', function () {
          reorderDetails = detailsInput.value;
          syncFormProperties();
        });
      }
      if (reorderCta) {
        reorderCta.addEventListener('click', function () {
          // Same real-button proxy pattern as the normal review CTA.
          var form = findProductForm();
          var realBtn = document.getElementById('pls-main-add-btn') ||
            (form && form.querySelector('[type="submit"], button[name="add"]'));
          if (realBtn) realBtn.click();
        });
      }
    }

    // One shared snippets/ft-ai-generator.liquid panel serves every upload
    // block on the page — the widget's own JS (assets/ai-designer.js) only
    // ever looks for a single ".ai-chat", so there's exactly one instance to
    // go around regardless of how many print locations this product has.
    // Toggling a given side's "generate with AI" button relocates the panel
    // to sit right after THAT side's upload block (appendChild preserves
    // the widget's bound listeners/state — same pattern ft-order-wizard.js
    // already relies on for its own two widget roots) and records which
    // side is the current target, so the panel's own "Use this design"
    // button (added in ai-designer.js's renderAssistantTurn) knows where to
    // send the result without the two files needing to share more than
    // this one hand-off surface.
    function bindAiGenerateToggles() {
      var panel = root.querySelector('[data-ft-ai-panel]');
      if (!panel) return; // ft-ai-generator snippet not rendered on this page — fail quiet
      var backdrop = document.querySelector('[data-ft-ai-backdrop]');

      // Relocate both to direct children of <body>, once, on init — NOT
      // just for fixed-position stacking, but because this snippet renders
      // inside ft-mockup-builder.liquid, which itself renders inside the
      // product page's own <form action="/cart/add">. A <form> can't
      // legally nest inside another <form>; the browser silently drops an
      // illegally-nested one at parse time, which took the whole composer
      // (including the send button's native submit-on-click behaviour)
      // with it — confirmed live, [data-ai-form] didn't exist in the DOM
      // at all until this fix. Moving the panel out from under the
      // product form before assets/ai-designer.js's own IIFE runs its
      // querySelectors is what actually resolves it.
      if (backdrop && backdrop.parentNode !== document.body) document.body.appendChild(backdrop);
      if (panel.parentNode !== document.body) document.body.appendChild(panel);

      var targetLabelEl = panel.querySelector('[data-ft-ai-target-label]');
      var closeBtn = panel.querySelector('[data-ft-ai-close]');
      var aiChatRoot = panel.querySelector('.ai-chat');
      // document, not root: snippets/ft-ai-entry.liquid's top-of-page button
      // renders from product-template.liquid, above the colour/size picker —
      // outside this widget's own root entirely — but shares this one panel.
      var toggles = document.querySelectorAll('[data-ai-generate-toggle]');
      var currentSideId = null;
      // Set when "Use this design" is clicked with the __pending__ sentinel
      // (no print location chosen yet) — Roland's "design-first" entry point.
      // Consumed the moment the customer's first print-location design step
      // opens (see showSidePanel below), so they never have to re-attach it.
      var pendingAiDesign = null; // { url, filename } | null

      // "Make a matching design for the other print location" — once one
      // side already has art, the remaining toggles should say so plainly
      // instead of the generic "Or generate artwork with AI" (Roland's
      // 2026-08-31 feedback: the path to a second, matching design wasn't
      // discoverable). referenceSeeded tracks which sides we've already
      // auto-attached a reference image for, so reopening the same toggle
      // doesn't keep re-adding the same reference.
      var referenceSeeded = {};
      var toggleDefaultText = {};
      toggles.forEach(function (t) {
        toggleDefaultText[t.getAttribute('data-ai-generate-toggle')] = t.textContent;
      });

      function shortLabel(sideId) {
        var match = null;
        toggles.forEach(function (t) {
          if (t.getAttribute('data-ai-generate-toggle') === sideId) match = t;
        });
        var label = (match && match.getAttribute('data-ai-generate-label')) || sideId;
        return label.replace(/ print$/, '');
      }

      function refreshToggleCopy() {
        var anyArt = Object.keys(state.sides).some(function (id) {
          return state.sides[id] && state.sides[id].art;
        });
        toggles.forEach(function (t) {
          var sideId = t.getAttribute('data-ai-generate-toggle');
          if (sideId === '__pending__') return;
          var thisSideHasArt = state.sides[sideId] && state.sides[sideId].art;
          t.textContent = (anyArt && !thisSideHasArt)
            ? '✨ Generate a matching ' + shortLabel(sideId) + ' design with AI'
            : toggleDefaultText[sideId];
        });
      }
      root.__ftRefreshAiToggleCopy = refreshToggleCopy;
      refreshToggleCopy();

      function findExistingArtSide(excludeSideId) {
        var ids = Object.keys(state.sides);
        for (var i = 0; i < ids.length; i++) {
          var id = ids[i];
          if (id === excludeSideId) continue;
          var s = state.sides[id];
          if (s && s.art && (s.art.originalUrl || s.art.url)) return id;
        }
        return null;
      }

      // Give the AI something real to match against instead of just a text
      // prompt — attach the already-finished design from another print
      // location as a reference image the first time the panel opens for a
      // new one. Non-critical: if the fetch fails, the customer can still
      // generate normally, just without the auto-attached reference.
      function seedReferenceIfNeeded(sideId) {
        if (sideId === '__pending__' || referenceSeeded[sideId]) return;
        if (!aiChatRoot || !aiChatRoot.__ftAiAddReferenceFile) return;
        var existingId = findExistingArtSide(sideId);
        if (!existingId) return;
        referenceSeeded[sideId] = true;
        var art = state.sides[existingId].art;
        urlToFile(art.originalUrl || art.url, art.filename || 'design.png')
          .then(function (file) {
            aiChatRoot.__ftAiAddReferenceFile(
              file,
              'Added your ' + shortLabel(existingId) + ' design as a reference so this can match'
            );
          })
          .catch(function (e) {
            console.warn('[ft-mb] could not seed matching-design reference:', e);
          });
      }

      var entryBtn = document.querySelector('[data-ai-entry-btn]');
      var entryBtnText = entryBtn ? entryBtn.querySelector('[data-ai-entry-btn-text]') : null;

      function setEntryReady(ready) {
        if (!entryBtn || !entryBtnText) return;
        if (ready) {
          entryBtn.setAttribute('data-ai-entry-ready', '');
          entryBtnText.textContent = '✓ Design ready — pick your print location below to add it';
        } else {
          entryBtn.removeAttribute('data-ai-entry-ready');
          entryBtnText.textContent = '✨ Don’t have a design ready? Try our AI designer — see it on a shirt before you order';
        }
      }

      // Shown once the pending design has actually been attached
      // (2026-09-04, Roland's feedback: after picking a print location it
      // wasn't clear the design they'd just made was ever going to be
      // used). Distinct from setEntryReady(true) — that means "waiting to
      // be applied", this means "done, here's where it went" — so the one
      // place a customer would naturally look back to check ("did that
      // work?") gives them a real answer instead of reverting to the
      // generic "don't have a design yet" prompt mid-attach.
      function setEntryApplied(sideLabel) {
        if (!entryBtn || !entryBtnText) return;
        entryBtn.setAttribute('data-ai-entry-ready', '');
        entryBtnText.textContent = '✓ Design added to your ' + (sideLabel ? sideLabel.toLowerCase() : 'print') + ' — build the rest of your order below';
      }

      // The gap this fills (2026-09-04, follow-up to the auto-attach-on-
      // pill-click fix): attaching involves a real network round trip
      // (print-quality upgrade, then the upload itself) that can run
      // several seconds — with no state between "ready" and "added",
      // nothing on screen changed for that whole window, which reads as
      // "did my click even register?" This is deliberately innerHTML, not
      // textContent — it's the one state that needs a spinner glyph
      // alongside the text, reusing ft-mockup-builder.css's existing
      // .ft-mb__spinner rather than inventing a second one.
      function setEntryApplying() {
        if (!entryBtn || !entryBtnText) return;
        entryBtn.removeAttribute('data-ai-entry-ready');
        entryBtnText.innerHTML = '<span class="ft-mb__spinner" aria-hidden="true"></span>Attaching your design…';
      }

      function closePanel() {
        panel.hidden = true;
        if (backdrop) backdrop.hidden = true;
        toggles.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
        currentSideId = null;
      }

      toggles.forEach(function (toggle) {
        toggle.addEventListener('click', function () {
          var sideId = toggle.getAttribute('data-ai-generate-toggle');
          if (currentSideId === sideId && !panel.hidden) { closePanel(); return; }
          currentSideId = sideId;
          toggles.forEach(function (t) { t.setAttribute('aria-expanded', String(t === toggle)); });
          if (targetLabelEl) targetLabelEl.textContent = toggle.getAttribute('data-ai-generate-label') || sideId;
          panel.hidden = false;
          if (backdrop) backdrop.hidden = false;
          seedReferenceIfNeeded(sideId);
        });
      });

      if (closeBtn) closeBtn.addEventListener('click', closePanel);
      if (backdrop) backdrop.addEventListener('click', closePanel);

      function urlToFile(url, filename) {
        return fetch(url)
          .then(function (r) {
            if (!r.ok) throw new Error('Could not fetch the generated design');
            return r.blob();
          })
          .then(function (blob) {
            return new File([blob], filename || 'ai-design.png', { type: blob.type || 'image/png' });
          });
      }

      // AI chat drafts come back at 1024² (fast/cheap, fine for previewing
      // in conversation) — nowhere near enough for a real print at most
      // sizes (confirmed live 2026-09-03: a "Large Front Print" attach came
      // through at ~81 DPI). assets/ai-design-attach.js already solved this
      // for the OTHER attach path (print-location-selector.liquid) by
      // calling the worker's /api/print-upgrade to regenerate the same
      // design at 2880² before attaching — this widget's own attach path
      // (the embedded per-location panel + the design-first entry point)
      // never had that step wired in, which is the actual bug. Best-effort
      // and non-blocking to match that same file's philosophy: any failure
      // (no worker URL configured, network error, timeout) just falls back
      // to the original draft URL rather than stopping the customer's
      // order over a quality upgrade that failed.
      function upgradeToPrintQuality(draftUrl, prompt) {
        var workerUrl = ((aiChatRoot && aiChatRoot.dataset && aiChatRoot.dataset.workerUrl) || '').replace(/\/$/, '');
        if (!workerUrl || !draftUrl) return Promise.resolve(draftUrl);
        return new Promise(function (resolve) {
          var settled = false;
          function fallback() {
            if (settled) return;
            settled = true;
            resolve(draftUrl);
          }
          var xhr = new XMLHttpRequest();
          xhr.open('POST', workerUrl + '/api/print-upgrade', true);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.setRequestHeader('Accept', 'application/json');
          xhr.timeout = 90000; // print regen at 2880² can take 30-60s
          xhr.onload = function () {
            if (settled) return;
            try {
              var data = JSON.parse(xhr.responseText || '{}');
              if (xhr.status >= 200 && xhr.status < 300 && data.url) {
                settled = true;
                resolve(data.url);
              } else {
                fallback();
              }
            } catch (e) {
              fallback();
            }
          };
          xhr.onerror = fallback;
          xhr.ontimeout = fallback;
          xhr.send(JSON.stringify({ previousImageUrl: draftUrl, prompt: prompt || '' }));
        });
      }

      // Called from showSidePanel() every time a print location's design
      // step opens, AND from ft-order-builder.js's print-location pill click
      // handler directly (so it can attach a spinner + know when the design
      // is really in place). Selecting a pill triggers both in the same
      // tick — the pill's own checkbox 'change' event synchronously drives
      // the wizard to that side's design step (-> showSidePanel -> this),
      // and the click handler calls this again right after. Without
      // applyingPromise, the second caller would see pendingAiDesign already
      // consumed by the first and get back an instantly-resolved promise —
      // so anything awaiting ITS result (the pill's "Needs a design"/"✓
      // Design added" status badge) would resolve before the real upload
      // finished and never hear about the real completion. Sharing one
      // in-flight promise across both callers fixes that (confirmed via a
      // live repro, 2026-09-04).
      var applyingPromise = null;
      root.__ftApplyPendingAiDesign = function (sideId) {
        if (applyingPromise) return applyingPromise;
        if (!pendingAiDesign || (state.sides[sideId] && state.sides[sideId].art)) return Promise.resolve();
        var pending = pendingAiDesign;
        pendingAiDesign = null;
        setEntryApplying();
        applyingPromise = upgradeToPrintQuality(pending.url, pending.prompt)
          .then(function (printUrl) {
            return urlToFile(printUrl, pending.filename);
          })
          .then(function (file) {
            return handleFile(sideId, file);
          })
          .then(function () {
            refreshToggleCopy();
            var sideCfg = productCfg.sides[sideId];
            setEntryApplied(sideCfg && sideCfg.label);
          })
          .catch(function (e) {
            // Give the customer something instead of a silent no-op — the
            // upload block's own error slot is right here and already
            // styled for exactly this. Restore the entry button too, since
            // the design wasn't actually lost, just failed to fetch this
            // time — worth letting them retry from the top.
            console.error('[ft-mb] pending AI design fetch failed:', e);
            var metaEl = root.querySelector('[data-upload-meta="' + sideId + '"]');
            if (metaEl) metaEl.innerHTML = '<span class="ft-mb__err">Couldn’t load your AI design — please try generating it again.</span>';
            pendingAiDesign = pending;
            setEntryReady(true);
          })
          .finally(function () { applyingPromise = null; });
        return applyingPromise;
      };

      // Looked up fresh per call from ai-designer.js, not cached there —
      // script load order between the two files isn't guaranteed.
      root.__ftAiGenerateTarget = function () { return currentSideId; };
      root.__ftAttachGeneratedArt = function (sideId, url, filename, prompt) {
        if (sideId === '__pending__') {
          pendingAiDesign = { url: url, filename: filename, prompt: prompt };
          setEntryReady(true);
          closePanel();
          return Promise.resolve();
        }
        return upgradeToPrintQuality(url, prompt)
          .then(function (printUrl) { return urlToFile(printUrl, filename); })
          .then(function (file) { return handleFile(sideId, file); })
          .then(function () { closePanel(); refreshToggleCopy(); });
      };
    }

    function finalizeSidesChoice() {
      var newSides = wizard.pendingSides.slice();
      // A side that was chosen before and had artwork, but just got
      // deselected, shouldn't silently stay attached (and priced).
      wizard.chosenSides.forEach(function (sideId) {
        if (newSides.indexOf(sideId) === -1 && state.sides[sideId].art) clearArt(sideId);
      });
      wizard.chosenSides = newSides;
      wizard.steps = computeWizardSteps(newSides);
    }

    function goToWizardStep(index) {
      wizard.currentIndex = index;
      var stepId = wizard.steps[index];
      var topLevel = stepId.split(':')[0]; // 'sides' | 'design' | 'review'

      root.querySelectorAll('[data-wizard-step]').forEach(function (el) {
        el.hidden = el.dataset.wizardStep !== topLevel;
      });

      if (topLevel === 'sides') {
        var choiceSideId = stepId.split(':')[1];
        var choiceSideCfg = productCfg.sides[choiceSideId];
        showLocationPanel(choiceSideId);
        var sidesTitleEl = root.querySelector('[data-sides-title]');
        var sidesSubEl = root.querySelector('[data-sides-sub]');
        if (sidesTitleEl) sidesTitleEl.textContent = 'Would you like a print on the ' + choiceSideCfg.label.toLowerCase() + '?';
        if (sidesSubEl) sidesSubEl.textContent = 'Pick a location, or skip it.';
      }

      if (topLevel === 'design') {
        var sideId = stepId.split(':')[1];
        state.activeSide = sideId;
        showSidePanel(sideId);
        var titleEl = root.querySelector('[data-design-title]');
        if (titleEl) {
          var designSideCfg = productCfg.sides[sideId];
          var designSideLabel = designSideCfg && designSideCfg.label ? designSideCfg.label.toLowerCase() : sideId;
          // "Add your X design" reads like a design tool ("we'll design it
          // for you") — this widget only ever takes an upload and places
          // it, so orderBuilderMode says "Upload" instead. Left unchanged
          // for every other mockup-ready product, which isn't part of
          // today's ask and stays live/unaffected.
          titleEl.textContent = orderBuilderMode
            ? ('Upload your ' + designSideLabel + ' print')
            : ('Add your ' + designSideLabel + ' design');
        }
        // The stage-wrap sits inside this step, which is `hidden` (and so
        // has clientWidth 0) until now. fitStageToWrap()'s very first call
        // during init happens before any step is visible, measures 0, and
        // bails — leaving the canvas at its full unscaled 1896x1896px,
        // clipped down to whatever the container's actual width turns out
        // to be. Re-measure now that this step's container is real.
        fitStageToWrap();
        renderSide();
        updateSizeChip(state.sides[sideId]);
      }

      if (topLevel === 'review') populateReview();

      updateWizardProgress();
      updateWizardNav(); // also notifies wizardChangeListeners — see the comment inside it
    }

    // Forces goToWizardStep to re-run against the CURRENT step, unchanged
    // — used by the ft-order-wizard orchestrator right after it unhides
    // this whole widget's root. Needed because the live PLS-checkbox
    // listener below already calls goToWizardStep(0) the instant a
    // print-location pill is toggled, which happens while this widget's
    // root is still hidden under the orchestrator's shirts/locations
    // step — so fitStageToWrap() (see the comment a few lines up) measures
    // a 0-width container and bails on that first call. Calling this
    // again once the container is genuinely visible re-measures for real.
    function reenter() {
      goToWizardStep(wizard.currentIndex);
    }

    function notifyWizardChange() {
      wizardChangeListeners.forEach(function (cb) { cb(); });
    }

    // Friendly name for any step in wizard.steps, by index — used to build
    // "Next: X" / "Back: X" button labels rather than a bare "Next →".
    // Returns null for a step this shouldn't ever label (or an
    // out-of-range index, e.g. asking for the step before 0).
    function getStepLabel(index) {
      var stepId = wizard.steps[index];
      if (!stepId) return null;
      if (stepId === 'review') return 'Review';
      // Every design step is an upload step from the customer's point of
      // view — say "Upload" rather than the print-location name (e.g.
      // "Next: Front" was confusing next to the location already picked
      // via the print-location pills; "Next: Upload" describes the action).
      if (stepId.indexOf('design:') === 0) return 'Upload';
      return null; // 'sides:X' steps aren't relevant in orderBuilderMode
    }

    // The actual print-location name for a step (e.g. "Left Sleeve"), as
    // opposed to getStepLabel's generic "Upload" — used for page-level
    // headings ("Design your Left Sleeve print") where naming the specific
    // location is the whole point, unlike the Next/Back button where the
    // action ("Upload") reads clearer than the location name.
    function getStepSideLabel(index) {
      var stepId = wizard.steps[index];
      if (!stepId) return null;
      if (stepId === 'review') return 'Review';
      if (stepId.indexOf('design:') === 0) {
        var sideCfg = productCfg.sides[stepId.split(':')[1]];
        return sideCfg && sideCfg.label ? sideCfg.label : null;
      }
      return null;
    }

    // Read-only mirror of updateWizardNav()'s own computation — kept as a
    // single source of truth there (this just reads the same wizard/state
    // this file already owns) so the ft-order-wizard orchestrator's shared
    // Back/Next buttons show correct disabled/label state without
    // reimplementing this gating logic a second time.
    function getNavState() {
      var stepId = wizard.steps[wizard.currentIndex];
      var backStepLabel = getStepLabel(wizard.currentIndex - 1);
      if (stepId === 'review' || stepId.indexOf('sides:') === 0) {
        return { backVisible: wizard.currentIndex !== 0, nextVisible: false, nextDisabled: true, nextLabel: 'Next →', backStepLabel: backStepLabel, nextStepLabel: null };
      }
      var uploading = false;
      if (stepId.indexOf('design:') === 0) {
        var s = state.sides[stepId.split(':')[1]];
        uploading = !!(s.art && !s.art.url);
      }
      var nextStepLabel = getStepLabel(wizard.currentIndex + 1);
      return {
        backVisible: wizard.currentIndex !== 0,
        nextVisible: true,
        nextDisabled: !isWizardStepReady(stepId),
        nextLabel: uploading ? 'Uploading…' : 'Next →',
        backStepLabel: backStepLabel,
        nextStepLabel: uploading ? null : nextStepLabel
      };
    }

    function updateWizardNav() {
      var backBtn = root.querySelector('[data-wizard-back]');
      var nextBtn = root.querySelector('[data-wizard-next]');
      var stepId = wizard.steps[wizard.currentIndex];

      // Single authoritative point this function is called from both
      // goToWizardStep AND syncFormProperties (the latter runs after
      // every art upload/colour change/checkbox toggle, not just step
      // transitions) — notifying here, rather than only from
      // goToWizardStep, is what keeps the ft-order-wizard orchestrator's
      // shared Next button in sync with readiness changes that happen
      // WITHIN a design step (e.g. an upload finishing async), not just
      // between steps.
      notifyWizardChange();

      // The theme's own `button { display: inline-block }` rule is an
      // author-origin style, which beats the browser's default
      // `[hidden] { display: none }` (user-agent origin) regardless of
      // selector specificity — so `.hidden` alone doesn't actually hide
      // a <button> here. Set display directly instead.
      backBtn.style.display = wizard.currentIndex === 0 ? 'none' : '';

      if (stepId === 'review' || stepId.indexOf('sides:') === 0) {
        nextBtn.style.display = 'none';
        return;
      }
      nextBtn.style.display = '';
      nextBtn.disabled = !isWizardStepReady(stepId);

      if (stepId.indexOf('design:') === 0) {
        var s = state.sides[stepId.split(':')[1]];
        nextBtn.textContent = (s.art && !s.art.url) ? 'Uploading\u2026' : 'Next \u2192';
      } else {
        nextBtn.textContent = 'Next \u2192';
      }
    }

    function updateWizardProgress() {
      var progressEl = root.querySelector('[data-wizard-progress]');
      if (!progressEl) return;
      progressEl.innerHTML = '';
      wizard.steps.forEach(function (stepId, i) {
        var dot = document.createElement('li');
        dot.className = 'ft-mb__progress-dot';
        if (i === wizard.currentIndex) dot.classList.add('is-current');
        else if (i < wizard.currentIndex) dot.classList.add('is-done');
        progressEl.appendChild(dot);
      });
    }

    async function populateReview() {
      var grid = root.querySelector('[data-review-grid]');
      grid.innerHTML = '';
      for (var i = 0; i < wizard.chosenSides.length; i++) {
        var sideId = wizard.chosenSides[i];
        var s = state.sides[sideId];
        var card = document.createElement('div');
        card.className = 'ft-mb__review-card';
        if (s.art && s.image && s.placement) {
          var dataUrl = await renderSnapshotSide(sideId);
          var img = document.createElement('img');
          img.src = dataUrl;
          img.alt = capitalise(sideId) + ' preview';
          var label = document.createElement('span');
          label.textContent = capitalise(sideId) + ' \u2014 ' + zoneFor(sideId).label +
            ' (' + s.printMm.w + '\u00d7' + s.printMm.h + 'mm)';
          var downloadLink = document.createElement('a');
          downloadLink.className = 'ft-mb__review-download';
          downloadLink.href = dataUrl;
          downloadLink.download = '60ft-ink-' + sideId + '-mockup.png';
          downloadLink.textContent = 'Download this preview';
          card.appendChild(img);
          card.appendChild(label);
          card.appendChild(downloadLink);
        } else {
          card.textContent = capitalise(sideId) + ': no artwork yet';
          card.classList.add('ft-mb__review-card--missing');
        }
        grid.appendChild(card);
      }

      // Prefer the size/quantity grid's own combined total (garments +
      // print add-ons + bulk discount, already correctly calculated there)
      // so the last screen before Add to Cart shows one real number, not
      // just the print portion. Falls back to the print-only figure if a
      // customer somehow reaches review before picking any quantities.
      var priceEl = root.querySelector('[data-review-price]');
      if (orderBuilderMode) {
        // ft-order-builder.js owns the real total here (size-quantity-
        // matrix.liquid isn't rendered on this product at all, so the
        // element IDs the non-order-builder branch below reads never
        // exist) — this used to silently fall through to an empty string.
        var obQtyEl = document.querySelector('[data-ob-total-qty]');
        var obPriceEl = document.querySelector('[data-ob-total-price]');
        var obPerShirtEl = document.querySelector('[data-ob-per-shirt]');
        if (obQtyEl && obPriceEl && obQtyEl.textContent !== '0') {
          priceEl.textContent = obQtyEl.textContent + ' items — ' + obPriceEl.textContent + ' total' +
            (obPerShirtEl && obPerShirtEl.textContent ? ' ' + obPerShirtEl.textContent : '');
        } else {
          priceEl.textContent = '';
        }
        return;
      }
      var totalQtyEl = document.querySelector('[id^="size-matrix-total-qty-"]');
      var totalPriceEl = document.querySelector('[id^="size-matrix-total-price-"]');
      if (totalQtyEl && totalPriceEl && totalQtyEl.textContent !== '0') {
        priceEl.textContent = totalQtyEl.textContent + ' items — ' + totalPriceEl.textContent + ' total';
      } else {
        var plsTotal = document.getElementById('pls-summary-total');
        priceEl.textContent = (plsTotal && plsTotal.textContent)
          ? ('Print add-ons: ' + plsTotal.textContent + ' — pick your sizes above to see the full total')
          : '';
      }
    }

    /* ========================================================
     * Placement + DPI maths
     * ======================================================== */

    function zoneFor(sideId) {
      return productCfg.sides[sideId].zones[state.sides[sideId].zoneId];
    }

    // px-per-mm calibration for a zone (template pixels per real-world mm)
    function pxPerMmFor(z) {
      return z.rect.width / z.print_mm.width;
    }

    // Printed mm + effective DPI for a given on-canvas display size
    function computeDpi(sideId, dispW, dispH) {
      var s = state.sides[sideId];
      var z = zoneFor(sideId);
      var pxPerMm = pxPerMmFor(z);
      var printW = dispW / pxPerMm;
      var printH = dispH / pxPerMm;
      var dpi = Math.round(s.art.naturalW / (printW / 25.4));
      return { printMm: { w: Math.round(printW), h: Math.round(printH) }, dpi: dpi };
    }

    // Auto-fit artwork into the zone (contain), reset any prior manual
    // placement, compute printed mm + effective DPI
    function recomputeSide(sideId) {
      var s = state.sides[sideId];
      if (!s.art) { s.dpi = null; s.printMm = null; s.placement = null; return; }

      var z = zoneFor(sideId);
      // Default to a bit under the maximum contain-fit, not the absolute
      // max — a "contain" fit by definition already touches one zone edge,
      // so starting there would leave zero room to resize larger.
      var r = Math.min(z.rect.width / s.art.naturalW, z.rect.height / s.art.naturalH) * DEFAULT_FIT_RATIO;
      s.fit = { dispW: s.art.naturalW * r, dispH: s.art.naturalH * r };
      s.placement = null; // materialised (centred/top-aligned) on next render
      s.selected = true; // show resize handles right away on a fresh placement

      var dpiInfo = computeDpi(sideId, s.fit.dispW, s.fit.dispH);
      s.printMm = dpiInfo.printMm;
      s.dpi = dpiInfo.dpi;
      updateDpiBadge(sideId);
    }

    // "Max size" button — jumps straight to the full contain-fit (the same
    // maths as recomputeSide, just without the headroom ratio), centred/
    // top-aligned per the zone's own alignment rule. Customers can still
    // drag the resize handles down afterwards if they want it smaller.
    function maxOutArt(sideId) {
      var s = state.sides[sideId];
      if (!s.art || !s.image) return;

      var z = zoneFor(sideId);
      var r = Math.min(z.rect.width / s.art.naturalW, z.rect.height / s.art.naturalH);
      var dispW = s.art.naturalW * r;
      var dispH = s.art.naturalH * r;
      var x = z.rect.x + (z.rect.width - dispW) / 2;
      var y = z.align === 'top-center' ? z.rect.y : z.rect.y + (z.rect.height - dispH) / 2;

      s.fit = { dispW: dispW, dispH: dispH };
      s.placement = { x: x, y: y, width: dispW, height: dispH };
      s.selected = true;

      var dpiInfo = computeDpi(sideId, dispW, dispH);
      s.printMm = dpiInfo.printMm;
      s.dpi = dpiInfo.dpi;
      updateDpiBadge(sideId);

      if (state.activeSide === sideId) renderSide();
      syncFormProperties();
    }

    // Offset of the artwork's top-left corner from the zone's top-left
    // corner, in real-world mm — this is what the renderer needs alongside
    // print_mm to reproduce the exact placement the customer chose.
    function offsetMmFor(sideId) {
      var s = state.sides[sideId];
      var z = zoneFor(sideId);
      if (!s.placement) return { x: 0, y: 0 };
      var pxPerMm = pxPerMmFor(z);
      return {
        x: Math.round((s.placement.x - z.rect.x) / pxPerMm),
        y: Math.round((s.placement.y - z.rect.y) / pxPerMm)
      };
    }

    function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }

    // Persist the live node position/size back into state, recompute DPI
    // from the actual current size (post-drag/resize), and re-gate the form.
    function finalizePlacement(sideId, node) {
      var s = state.sides[sideId];
      s.placement = { x: node.x(), y: node.y(), width: node.width(), height: node.height() };
      var dpiInfo = computeDpi(sideId, node.width(), node.height());
      s.printMm = dpiInfo.printMm;
      s.dpi = dpiInfo.dpi;
      updateDpiBadge(sideId);
      syncFormProperties();
    }

    // Live DPI feedback while resizing, without the overhead of a full
    // form/property sync on every animation frame
    function previewLiveDpi(sideId, dispW, dispH) {
      var s = state.sides[sideId];
      var dpiInfo = computeDpi(sideId, dispW, dispH);
      s.printMm = dpiInfo.printMm;
      s.dpi = dpiInfo.dpi;
      updateDpiBadge(sideId);
    }

    // Customer-facing DPI badge removed entirely (2026-09-03, Roland's
    // feedback) \u2014 even the "good to print" green state read as a
    // technical red flag / confusion point, and the policy here is "let
    // them upload anything, staff sort out quality and any redraw fees
    // afterwards" (see getQualityIssues' no-manual-work comment below).
    // s.dpi itself is untouched and still computed on every resize/crop \u2014
    // it still drives the staff-facing "Print Quality" / "Design review
    // needed" order properties (buildPrintQualitySummary,
    // getQualityIssues) and the customer status line's gentle "flagged
    // for a quick check by our designer" message. Only this per-side
    // badge element is gone; kept as a no-op (rather than deleting every
    // call site) so it's a one-function revert if ever wanted back.
    function updateDpiBadge(sideId) {
      var el = root.querySelector('[data-dpi="' + sideId + '"]');
      if (!el) return;
      el.hidden = true;
      el.className = 'ft-mb__dpi';
      if (sideId === state.activeSide) updateSizeChip(state.sides[sideId]);
    }

    function updateSizeChip(s) {
      var chip = root.querySelector('[data-size-chip]');
      var maxBtn = root.querySelector('[data-max-size]');
      if (chip) {
        if (!s.art || !s.printMm) { chip.hidden = true; }
        else { chip.hidden = false; chip.textContent = 'Final print size: ' + s.printMm.w + ' \u00d7 ' + s.printMm.h + 'mm'; }
      }
      if (maxBtn) maxBtn.hidden = !s.art;
    }

    /* ========================================================
     * Rendering
     * ======================================================== */

    async function renderSide() {
      var sideId = state.activeSide;
      var sideCfg = productCfg.sides[sideId];
      var tplUrl = sideCfg.templates[state.colour] || sideCfg.templates._default;

      if (loadingEl) loadingEl.style.display = 'block';
      var tplImg = templateCache[tplUrl] || (templateCache[tplUrl] = await loadImage(tplUrl, true).catch(function () { return null; }));
      if (loadingEl) loadingEl.style.display = 'none';

      if (tplImg) {
        templateNode.image(tplImg);
        templateNode.visible(true);
      } else {
        templateNode.visible(false); // template missing \u2014 still show art + zone guide
      }

      artLayer.destroyChildren();
      hideCenterGuides();

      var s = state.sides[sideId];
      var z = zoneFor(sideId);

      // zone guide
      zoneGuide.setAttrs({ x: z.rect.x, y: z.rect.y, width: z.rect.width, height: z.rect.height, visible: true });

      if (s.image && s.fit) {
        if (!s.placement) {
          // materialise the default (centred, or top-aligned per zone config)
          var defaultX = z.rect.x + (z.rect.width - s.fit.dispW) / 2;
          var defaultY = z.align === 'top-center'
            ? z.rect.y
            : z.rect.y + (z.rect.height - s.fit.dispH) / 2;
          s.placement = { x: defaultX, y: defaultY, width: s.fit.dispW, height: s.fit.dispH };
        }

        var artNode = new Konva.Image({
          image: s.image,
          x: s.placement.x,
          y: s.placement.y,
          width: s.placement.width,
          height: s.placement.height,
          draggable: true
        });

        artNode.dragBoundFunc(function (pos) {
          // Konva passes/expects `pos` in absolute stage-pixel space (already
          // scaled by stage.scale()), but zone rects are defined in the
          // unscaled design-unit space (the canvas's native 1896px grid).
          // Convert in, clamp/snap in design units, convert back out.
          var scaleFactor = stage.scaleX();
          var w = artNode.width();
          var h = artNode.height();
          var lx = pos.x / scaleFactor;
          var ly = pos.y / scaleFactor;

          var x = w <= z.rect.width
            ? clamp(lx, z.rect.x, z.rect.x + z.rect.width - w)
            : z.rect.x + (z.rect.width - w) / 2;
          var y = h <= z.rect.height
            ? clamp(ly, z.rect.y, z.rect.y + z.rect.height - h)
            : z.rect.y + (z.rect.height - h) / 2;

          var centerX = z.rect.x + z.rect.width / 2;
          var centerY = z.rect.y + z.rect.height / 2;
          var snapX = Math.abs((x + w / 2) - centerX) < SNAP_PX;
          var snapY = Math.abs((y + h / 2) - centerY) < SNAP_PX;
          if (snapX) x = centerX - w / 2;
          if (snapY) y = centerY - h / 2;
          showCenterGuides(z, snapX, snapY);

          return { x: x * scaleFactor, y: y * scaleFactor };
        });

        artNode.on('dragend', function () {
          hideCenterGuides();
          finalizePlacement(sideId, artNode);
        });

        artNode.on('mouseenter', function () { stage.container().style.cursor = 'move'; });
        artNode.on('mouseleave', function () { stage.container().style.cursor = 'default'; });

        artNode.on('click tap', function (e) {
          s.selected = true;
          transformer.visible(true);
          artLayer.batchDraw();
          e.cancelBubble = true; // don't let this bubble to the stage's deselect handler
        });

        var transformer = new Konva.Transformer({
          nodes: [artNode],
          visible: s.selected !== false,
          keepRatio: true,
          enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
          rotateEnabled: false,
          anchorSize: 14,
          anchorStroke: '#e2711d',
          anchorFill: '#fff',
          anchorCornerRadius: 3,
          borderStroke: 'rgba(0,0,0,0.4)',
          boundBoxFunc: function (oldBox, newBox) {
            // Transformer's boxes are also in absolute stage-pixel space —
            // scale the zone bounds up to match rather than converting the
            // box (simpler, and boundBoxFunc's return is used as-is by Konva).
            var scaleFactor = stage.scaleX();
            var zoneX = z.rect.x * scaleFactor;
            var zoneY = z.rect.y * scaleFactor;
            var zoneW = z.rect.width * scaleFactor;
            var zoneH = z.rect.height * scaleFactor;
            var minSize = Math.min(zoneW, zoneH) * 0.15;
            if (newBox.width < minSize || newBox.height < minSize) return oldBox;
            if (newBox.x < zoneX || newBox.y < zoneY ||
                newBox.x + newBox.width > zoneX + zoneW ||
                newBox.y + newBox.height > zoneY + zoneH) {
              return oldBox;
            }
            return newBox;
          }
        });

        artNode.on('transform', function () {
          previewLiveDpi(sideId, artNode.width() * artNode.scaleX(), artNode.height() * artNode.scaleY());
        });

        artNode.on('transformend', function () {
          // fold scale into width/height so placement stays scale-free
          var w = artNode.width() * artNode.scaleX();
          var h = artNode.height() * artNode.scaleY();
          artNode.scaleX(1);
          artNode.scaleY(1);
          artNode.width(w);
          artNode.height(h);
          finalizePlacement(sideId, artNode);
        });

        artLayer.add(artNode);
        artLayer.add(transformer);
        currentTransformer = transformer;
      } else {
        currentTransformer = null;
      }

      stage.batchDraw();
    }

    /* ========================================================
     * Product form integration (line item properties)
     * ======================================================== */

    function findProductForm() {
      var forms = Array.prototype.slice.call(document.querySelectorAll('form[action*="/cart/add"]'));
      if (forms.length <= 1) return forms[0] || null;

      // More than one add-to-cart form in the DOM (quick-shop modals on
      // related-product strips, or a sticky buy bar). Drop forms inside known
      // non-product containers, then lock onto the theme's real product form.
      var EXCLUDE = '.quick-shop-modal,.drawer,#CartDrawer,[data-quick-shop],template';
      var pool = forms.filter(function (f) { return !(f.closest && f.closest(EXCLUDE)); });
      if (!pool.length) pool = forms;

      // This theme renders the product form as `.product-single__form` with a
      // `[data-add-to-cart]` (name="add") submit; prefer it, else first in DOM.
      return pool.filter(function (f) {
        return f.classList.contains('product-single__form') ||
               f.querySelector('[data-add-to-cart],button[name="add"]');
      })[0] || pool[0];
    }

    function ensureHiddenInput(form, name) {
      var input = form.querySelector('input[name="' + cssEscape(name) + '"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }
      return input;
    }

    function buildDesignSpec() {
      var spec = {
        v: 1,
        product: productKey,
        preview_colour: state.colour,
        sides: {}
      };
      Object.keys(state.sides).forEach(function (sideId) {
        var s = state.sides[sideId];
        if (!s.art || !s.art.url) return; // only committed, uploaded art
        var z = zoneFor(sideId);
        spec.sides[sideId] = {
          zone: s.zoneId,
          zone_label: z.label,
          artwork_url: s.art.url,
          artwork_key: s.art.key,
          // The customer's unedited upload, before any auto-trim/background
          // removal — falls back to the same (print-ready) url when nothing
          // was trimmed, since in that case they're identical anyway.
          original_url: s.art.originalUrl || s.art.url,
          natural_px: [s.art.naturalW, s.art.naturalH],
          print_mm: [s.printMm.w, s.printMm.h],
          // Top-left offset of the artwork from the zone's top-left corner,
          // in mm. Source of truth for placement now that art is
          // draggable/resizable — the renderer should composite using
          // offset_mm + print_mm, not assume centred/top-aligned.
          offset_mm: [offsetMmFor(sideId).x, offsetMmFor(sideId).y],
          dpi: s.dpi
        };
      });
      return spec;
    }

    function designSummary(spec) {
      var parts = [];
      Object.keys(spec.sides).forEach(function (sideId) {
        parts.push(capitalise(sideId) + ': ' + spec.sides[sideId].zone_label);
      });
      return parts.length ? parts.join(' + ') : 'None';
    }

    // Everything worth a staff member's attention before this prints —
    // combines the low-DPI check above (using the more inclusive warn_below
    // threshold here, not block_below, since the cost of over-flagging is
    // just "someone double-checks it" while under-flagging means a real
    // problem ships unnoticed) with the background-removal check from
    // applyArt. One list so both the order note and the customer-facing
    // status line stay in sync with the same underlying signal.
    // Each issue carries a `type` so callers can filter — the customer
    // status line no longer mentions DPI at all (Roland's 2026-09-04
    // simplification pass: it's advisory-only and staff already assess it
    // during print prep, so surfacing "~111 DPI" to the customer was just
    // technical noise with no action for them to take). Staff-facing order
    // properties (reviewReasons below, buildPrintQualitySummary) still get
    // every issue, DPI included — they're the ones who actually act on it.
    function getQualityIssues() {
      var issues = [];
      Object.keys(state.sides).forEach(function (sideId) {
        var s = state.sides[sideId];
        if (!s.art) return;
        var label = capitalise(sideId);
        if (s.dpi !== null && s.dpi < dpiCfg.warn_below) {
          issues.push({ type: 'dpi', text: label + ': low resolution (~' + s.dpi + ' DPI)' });
        }
        if (s.art.bgLikelyNotRemoved) {
          issues.push({ type: 'background', text: label + ': background may not be fully removed' });
        }
      });
      return issues;
    }

    // Plain-text DPI per side, for staff — so this is readable directly on
    // the order without anyone needing to open the _ft_design JSON to find
    // it, which is where this only used to live.
    function buildPrintQualitySummary() {
      var parts = [];
      Object.keys(state.sides).forEach(function (sideId) {
        var s = state.sides[sideId];
        if (!s.art || s.dpi === null) return;
        var flag = s.dpi < dpiCfg.warn_below ? ' ⚠ low resolution' : '';
        parts.push(capitalise(sideId) + ': ' + s.dpi + ' DPI' + flag);
      });
      return parts.join('. ');
    }

    function syncFormProperties() {
      var form = findProductForm();

      // Reorder path — staff-assisted: customer wants the same design as a
      // named past order, no new artwork to upload. Short-circuits every
      // art/copyright-based rule below (this is the ONLY place `blocked`
      // gets computed and pushed to both this widget's own review CTA and
      // the real page-level add-to-cart button, so branching here is
      // enough to cover every entry point into checkout). Clears the
      // normal-flow properties so a toggled-on reorder item never carries
      // stale artwork data alongside its reorder note; clears the reorder
      // properties in the non-reorder branch below for the same reason in
      // the other direction.
      if (reorderMode) {
        if (form) {
          ensureHiddenInput(form, 'properties[_ft_design]').value = '';
          ensureHiddenInput(form, 'properties[Print Locations]').value = '';
          ensureHiddenInput(form, 'properties[Preview Colour]').value = '';
          ensureHiddenInput(form, 'properties[Copyright Confirmation]').value = '';
          ensureHiddenInput(form, 'properties[Print Quality]').value = '';
          ensureHiddenInput(form, 'properties[Design review needed]').value = '';
          // On order-builder-ready products (this widget's only real users
          // so far), print-location-selector.liquid shares this same form
          // and independently stamps its own "Will upload after ordering"
          // status the moment a location's checked — it has no way to know
          // THIS widget's reorder toggle is what's actually driving
          // checkout here, so this replaces that with a reorder note
          // instead. Must stay NON-EMPTY for whichever locations are
          // actually checked, not blanked — the cart-page reconciler in
          // ai-designer.js uses these exact property names as its only
          // signal that a shirt claims a given print; blanking them made
          // every reorder shirt look print-less and fired a false "Cart
          // needs review" warning on real orders (found 2026-08-27).
          var reorderArtworkNote = 'Reorder — see details above';
          var reorderPlsMap = {
            'Front artwork': 'pls-front',
            'Large Front artwork': 'pls-large-front',
            'Back artwork': 'pls-back',
            'Left sleeve artwork': 'pls-left-sleeve',
            'Right sleeve artwork': 'pls-right-sleeve'
          };
          Object.keys(reorderPlsMap).forEach(function (propName) {
            var cb = document.getElementById(reorderPlsMap[propName]);
            ensureHiddenInput(form, 'properties[' + propName + ']').value = (cb && cb.checked) ? reorderArtworkNote : '';
          });
          ensureHiddenInput(form, 'properties[Reorder — Previous order #]').value = reorderOrderNumber;
          ensureHiddenInput(form, 'properties[Reorder — Details]').value = reorderDetails;
        }
        // The order number/name field is NOT required (2026-09-03,
        // Roland's feedback) — but at least one print location IS
        // (2026-09-03, same day, urgent fix): that's the one signal that
        // actually drives whether a print-location fee gets added to the
        // cart at all — see buildReorderLocationChecklist's comment above
        // for the real orders this went out on with charges missing.
        var reorderReady = anyReorderLocationChecked();
        var reorderCta = root.querySelector('[data-reorder-cta]');
        if (reorderCta) reorderCta.disabled = !reorderReady;
        if (requireDesign && form) {
          var reorderSubmits = form.querySelectorAll('[type="submit"], button[name="add"], .size-qty-matrix__btn');
          reorderSubmits.forEach(function (submit) {
            submit.disabled = !reorderReady;
            submit.classList.toggle('ft-mb-blocked', !reorderReady);
          });
        }
        if (statusText && statusDot) {
          if (!reorderReady) {
            setStatus('wait', 'Pick at least one print location above to continue');
          } else {
            setStatus('good', reorderOrderNumber.trim()
              ? 'Reorder of ' + reorderOrderNumber.trim() + ' — ready to add to cart'
              : 'Reorder — ready to add to cart');
          }
        }
        return;
      }
      if (form) {
        ensureHiddenInput(form, 'properties[Reorder — Previous order #]').value = '';
        ensureHiddenInput(form, 'properties[Reorder — Details]').value = '';
      }

      var spec = buildDesignSpec();
      var hasArt = Object.keys(spec.sides).length > 0;
      var pendingUpload = Object.keys(state.sides).some(function (sideId) {
        var s = state.sides[sideId];
        return s.art && !s.art.url;
      });
      // Blocks the *real* add-to-cart button too, not just our own review
      // CTA — that button is rendered right after this widget on the page
      // and reachable directly without going through the wizard, so the
      // copyright confirmation has to gate it at the same point artwork
      // does. Quality issues (low DPI, background not removed) are
      // deliberately NOT part of this gate — see getQualityIssues — they
      // get flagged and reviewed, never blocked, per the no-manual-work
      // requirement: better to sell it and have staff catch it than lose
      // the sale over a fixable file problem.
      var blocked = !hasArt || pendingUpload || !copyrightConfirmed;

      syncPrintLocationSelector(spec);
      updateQtyPricingHint(hasArt);

      var reviewCta = root.querySelector('[data-review-cta]');
      if (reviewCta) reviewCta.disabled = blocked;

      var qualityIssues = getQualityIssues();

      if (form) {
        ensureHiddenInput(form, 'properties[_ft_design]').value = hasArt ? JSON.stringify(spec) : '';
        // Per-side link to the customer's unedited original upload, visible
        // (not underscore-prefixed) so staff can actually see and click it
        // on the order — same reasoning as the "artwork" status property
        // below: underscore-prefixed properties don't show on Shopify's
        // Admin order page at all. Only set for sides that actually have
        // art; a side never designed gets no property rather than a blank
        // one.
        Object.keys(spec.sides).forEach(function (sideId) {
          var side = spec.sides[sideId];
          ensureHiddenInput(form, 'properties[' + capitalise(sideId) + ' original file]').value =
            'Unedited original — ' + side.original_url;
        });
        ensureHiddenInput(form, 'properties[Print Locations]').value = designSummary(spec);
        ensureHiddenInput(form, 'properties[Preview Colour]').value = colourLabel(state.colour);
        ensureHiddenInput(form, 'properties[Copyright Confirmation]').value = copyrightConfirmed
          ? 'Customer confirmed ownership/rights to use the submitted artwork at ' + copyrightConfirmedAt
          : '';
        // Plain-text DPI per side — staff shouldn't need to open the
        // _ft_design JSON just to check print quality.
        ensureHiddenInput(form, 'properties[Print Quality]').value = buildPrintQualitySummary();
        // Combines system-detected issues (low DPI, background likely not
        // removed) with the customer's own opt-in checkbox into ONE
        // property, so staff have a single place to check "does this need
        // a look, and why" instead of two separate signals. Customer-visible
        // (not underscore-prefixed) — staff need to see this at a glance.
        var reviewReasons = qualityIssues.map(function (issue) { return issue.text; });
        if (designReviewRequested) reviewReasons.push('Customer requested a manual review');
        ensureHiddenInput(form, 'properties[Design review needed]').value = reviewReasons.length
          ? '⚠ ' + reviewReasons.join(' | ')
          : '';

        if (requireDesign) {
          // Native submit buttons on most product forms; `.size-qty-matrix__btn`
          // is a type="button" trigger used by the bulk/size-quantity-matrix
          // template's own JS (no name="add", doesn't submit natively).
          var submits = form.querySelectorAll('[type="submit"], button[name="add"], .size-qty-matrix__btn');
          submits.forEach(function (submit) {
            submit.disabled = blocked;
            submit.classList.toggle('ft-mb-blocked', blocked);
          });
        }
      }

      // status line \u2014 DPI issues are excluded here (staff assess print
      // quality anyway; see getQualityIssues' comment), so a low-res-only
      // upload reads as a plain "Design ready" instead of a false alarm.
      if (statusText && statusDot) {
        var customerIssues = qualityIssues.filter(function (issue) { return issue.type !== 'dpi'; }).map(function (issue) { return issue.text; });
        if (pendingUpload) {
          setStatus('wait', 'Uploading artwork\u2026');
        } else if (hasArt && !copyrightConfirmed) {
          setStatus('wait', 'Confirm the copyright checkbox above to continue');
        } else if (hasArt && customerIssues.length) {
          setStatus('warn', 'Design ready \u2014 flagged for a quick check by our designer (' + customerIssues.join('; ') + '). Minor touch-ups are free; if a full redraw\u2019s needed we\u2019ll contact you first to confirm before any extra work or cost.');
          if (uploadPreviews) snapshotPreviews(spec, form); // fire and forget
        } else if (hasArt) {
          setStatus('good', 'Design ready \u2014 ' + designSummary(spec));
          if (uploadPreviews) snapshotPreviews(spec, form); // fire and forget
        } else {
          setStatus('wait', requireDesign ? 'Upload artwork to continue' : 'No artwork uploaded yet');
        }
      }

      updateWizardNav();
    }

    // The pls widget's own checkbox/label/price/upload rows become a
    // second "pick your print location" UI once we're driving it — the
    // customer already made that call in the design tool above. Collapse
    // it to its own read-only summary + validation bar (both already
    // built into the widget, just normally hidden until something's
    // checked) so there's one obvious place to do the work.
    function simplifyPrintLocationSelector() {
      var plsWrap = document.getElementById('print-location-selector');
      if (!plsWrap) return;
      // pls's own stylesheet sets `.pls-options { display: flex }` — an
      // author rule at the same specificity as the browser's default
      // `[hidden] { display: none }`, and author rules always win over
      // that default regardless of source order. Setting the `hidden`
      // property/attribute alone does nothing here; an inline style is
      // needed to actually win the cascade.
      var optionsEl = plsWrap.querySelector('.pls-options');
      if (optionsEl) optionsEl.style.display = 'none';
      var subtextEl = plsWrap.querySelector('.pls-subtext');
      if (subtextEl) subtextEl.textContent = 'Reflects the print locations and pricing from the design builder above.';
      var headingEl = plsWrap.querySelector('.pls-heading');
      if (headingEl) headingEl.textContent = 'Your print locations';
      var requiredBadge = plsWrap.querySelector('.pls-required-badge');
      if (requiredBadge) requiredBadge.style.display = 'none';

      // This widget's own reorder toggle (above, in .ft-mb__confirm) is the
      // real one here — it's what drives the actual blocked/unblocked gate.
      // The pls widget's copy of the same toggle isn't hidden by the
      // .pls-options display:none above (it's a sibling element, not a
      // child of .pls-options), so without this it shows up a second time
      // in this now-read-only summary panel, right next to a checkbox that
      // does nothing here.
      var plsReorderToggle = plsWrap.querySelector('.pls-reorder-toggle');
      if (plsReorderToggle) plsReorderToggle.style.display = 'none';
      var plsReorderPanel = plsWrap.querySelector('.pls-reorder-panel');
      if (plsReorderPanel) plsReorderPanel.hidden = true;
    }

    // The size/quantity grid (snippets/size-quantity-matrix.liquid, a live
    // file we deliberately don't touch — it handles cart logic for every
    // Printable product, not just this one) already shows a live running
    // total that correctly includes print add-ons and the bulk-discount
    // tier once a print location is selected. Problem is it sits above
    // the design wizard on the page, so a customer sees a garment-only
    // number first, then watches it grow later — reads as "the price
    // changed on me." This just adds an honest "there's more to come"
    // note next to their total while that's still true; no math of ours.
    function updateQtyPricingHint(hasArt) {
      var summaryEl = document.querySelector('[id^="size-matrix-summary-"]');
      if (!summaryEl) return; // matrix not present on this page

      var hint = summaryEl.querySelector('[data-ft-price-hint]');
      if (!hint) {
        hint = document.createElement('div');
        hint.setAttribute('data-ft-price-hint', '');
        hint.className = 'ft-mb__price-hint';
        hint.textContent = '+ print costs — build your design below to see the full price';
        summaryEl.appendChild(hint);
      }
      hint.hidden = hasArt;
    }

    // PLS_ZONE_MAP now declared earlier, near wizard init — see there.

    function syncPrintLocationSelector(spec) {
      if (!document.getElementById('print-location-selector')) return; // not on this page

      var wanted = {}; // plsId -> spec.sides[sideId] entry driving it
      Object.keys(spec.sides).forEach(function (sideId) {
        var side = spec.sides[sideId];
        var plsId = (PLS_ZONE_MAP[sideId] || {})[side.zone];
        if (plsId) wanted[plsId] = side;
      });

      // order-builder mode: a side the customer has already chosen (via
      // ft-order-builder's pills, BEFORE any artwork exists) must keep its
      // checkbox checked here too — otherwise the block below, which only
      // knows about art-bearing sides via `spec.sides`, immediately
      // unchecks it again on every syncFormProperties() call, fighting
      // the widget's own toggle. `spec.sides` (built from uploaded art)
      // still wins once real art exists — this only covers the gap
      // between "chosen" and "designed".
      var chosenNoArtPlsIds = {};
      if (orderBuilderMode) {
        wizard.chosenSides.forEach(function (sideId) {
          if (spec.sides[sideId]) return; // already has art — handled by `wanted` above
          var zoneId = state.sides[sideId] && state.sides[sideId].zoneId;
          var plsId = zoneId && (PLS_ZONE_MAP[sideId] || {})[zoneId];
          if (plsId) chosenNoArtPlsIds[plsId] = true;
        });
      }

      ['pls-front', 'pls-large-front', 'pls-back', 'pls-left-sleeve', 'pls-right-sleeve'].forEach(function (plsId) {
        var checkbox = document.getElementById(plsId);
        if (!checkbox) return; // e.g. front options absent on promo-locked products
        var side = wanted[plsId];
        var urlInput = document.getElementById(plsId + '-url');
        var statusInput = document.getElementById(plsId + '-status-prop');
        var uploadArea = document.getElementById(plsId + '-upload');

        if (side) {
          // Populate before checking, so pls's own change handler sees the
          // URL already set and doesn't overwrite it with "Will upload
          // after ordering".
          if (urlInput) urlInput.value = side.artwork_url;
          // The real link used to live only in the underscore-prefixed
          // `_X artwork URL` property above — underscore-prefixed
          // properties are hidden from Shopify's Admin order page property
          // list (not just from the customer), so staff looking at this
          // order had no clickable link here at all, only a checkmark.
          // This is the one property for this side staff actually see.
          if (statusInput) statusInput.value = '✓ Received — ' + side.artwork_url;
          if (!checkbox.checked) {
            checkbox.checked = true;
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
          }
          // Our own upload flow (DPI check, crop, drag/resize) replaces
          // pls's basic upload box for whichever location we're driving.
          // Inline style, not just the hidden attribute — pls's own
          // change handler re-sets `hidden = false` on check, and an
          // inline style beats that regardless of attribute state.
          if (uploadArea) { uploadArea.style.display = 'none'; uploadArea.setAttribute('aria-hidden', 'true'); }
        } else if (chosenNoArtPlsIds[plsId]) {
          if (!checkbox.checked) {
            checkbox.checked = true;
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else if (checkbox.checked) {
          checkbox.checked = false;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    function setStatus(kind, text) {
      statusDot.className = 'ft-mb__status-dot ft-mb__status-dot--' + kind;
      statusText.textContent = text;
    }

    /* ---------- preview snapshots (best-effort) ---------- */

    // Renders one side onto the offscreen snapshot stage (not the visible
    // one) and returns a data URL — doesn't touch state.activeSide or
    // anything the customer can see.
    async function renderSnapshotSide(sideId) {
      var sideCfg = productCfg.sides[sideId];
      var tplUrl = sideCfg.templates[state.colour] || sideCfg.templates._default;
      var tplImg = templateCache[tplUrl] || (templateCache[tplUrl] = await loadImage(tplUrl, true).catch(function () { return null; }));

      snapshotTemplateNode.image(tplImg || null);
      snapshotTemplateNode.visible(!!tplImg);

      snapshotArtLayer.destroyChildren();
      var s = state.sides[sideId];
      if (s.image && s.placement) {
        snapshotArtLayer.add(new Konva.Image({
          image: s.image,
          x: s.placement.x,
          y: s.placement.y,
          width: s.placement.width,
          height: s.placement.height
        }));
      }
      snapshotStage.batchDraw();
      return snapshotStage.toDataURL({ pixelRatio: 0.5, mimeType: 'image/png' });
    }

    var snapshotTimer = null;
    function snapshotPreviews(spec, form) {
      clearTimeout(snapshotTimer);
      snapshotTimer = setTimeout(async function () {
        try {
          for (var i = 0, sides = Object.keys(spec.sides); i < sides.length; i++) {
            var sideId = sides[i];
            var dataUrl = await renderSnapshotSide(sideId);
            var blob = dataURLtoBlob(dataUrl);
            var file = new File([blob], 'preview-' + sideId + '.png', { type: 'image/png' });
            var uploaded = await uploadFile(uploadEndpoint, file);
            if (form) ensureHiddenInput(form, 'properties[_ft_preview_' + sideId + ']').value = uploaded.url;
          }
        } catch (e) {
          console.warn('[ft-mb] preview snapshot skipped:', e);
        }
      }, 800);
    }

    /* ---------- small helpers ---------- */

    function colourLabel(id) {
      if (!productCfg.colours) return id;
      var c = productCfg.colours.find(function (c) { return c.id === id; });
      return c ? c.label : id;
    }
  }

  /* ============================================================
   * Utilities
   * ============================================================ */

  function fetchJSON(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status + ' loading ' + url);
      return res.json();
    });
  }

  function loadImage(src, crossOrigin) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      if (crossOrigin) img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = reject;
      img.src = src;
    });
  }

  // "Magic wand" style clean-up, matching what the customer would do by
  // hand in Photoshop with the wand tolerance set to ~10: sample the
  // background colour from the four corners (uploads are almost always a
  // flat or transparent background around the actual design) and knock out
  // any pixel within tolerance of it — never the design itself, unless a
  // pixel in it happens to genuinely match the background colour. On black
  // garments, also knocks out near-black pixels anywhere in the image
  // (not just the background) since black ink doesn't print visibly on a
  // black shirt — the shirt colour shows through instead.
  var BG_WAND_TOLERANCE = 10;
  var BLACK_KNOCKOUT_TOLERANCE = 10;

  function channelMaxDiff(r1, g1, b1, r2, g2, b2) {
    return Math.max(Math.abs(r1 - r2), Math.abs(g1 - g2), Math.abs(b1 - b2));
  }

  function removeBackground(img, knockOutBlack) {
    var w = img.naturalWidth || img.width;
    var h = img.naturalHeight || img.height;
    if (!w || !h) return null;

    var canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);

    var imageData;
    try {
      imageData = ctx.getImageData(0, 0, w, h);
    } catch (e) {
      return null; // tainted canvas — skip clean-up rather than fail the upload
    }
    var data = imageData.data;

    // Corners that are ALREADY fully transparent carry no meaningful RGB —
    // many PNG exporters zero out (or otherwise garbage-fill) the colour
    // channels under alpha=0, and that "ghost" colour is often black. If we
    // sampled it anyway, we'd treat black as the background colour and the
    // wand pass below would strip every genuine black pixel in the design
    // (outlines, text, line art) right along with it — this was confirmed
    // as the cause of over-aggressive black removal on non-black garments
    // (2026-08-05): knockOutBlack is correctly gated to black shirts only,
    // but this corner-sampling step ran unconditionally regardless of
    // garment colour, so a transparent-cornered upload got its black
    // content wrongly wanded out on every shirt colour, not just black.
    var corners = [[0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1]];
    var br = 0, bg = 0, bb = 0, sampled = 0;
    corners.forEach(function (c) {
      var idx = (c[1] * w + c[0]) * 4;
      if (data[idx + 3] === 0) return; // ghost RGB, not a real background sample
      br += data[idx]; bg += data[idx + 1]; bb += data[idx + 2];
      sampled++;
    });
    if (sampled === 0) {
      // every corner is already transparent — nothing reliable to sample,
      // and the image most likely already has a clean transparent background
      return null;
    }
    br /= sampled; bg /= sampled; bb /= sampled;

    var changed = false;
    for (var i = 0; i < data.length; i += 4) {
      if (data[i + 3] === 0) continue; // already transparent
      var r = data[i], g = data[i + 1], b = data[i + 2];
      if (channelMaxDiff(r, g, b, br, bg, bb) <= BG_WAND_TOLERANCE) {
        data[i + 3] = 0;
        changed = true;
        continue;
      }
      if (knockOutBlack && channelMaxDiff(r, g, b, 0, 0, 0) <= BLACK_KNOCKOUT_TOLERANCE) {
        data[i + 3] = 0;
        changed = true;
      }
    }

    if (!changed) return null; // nothing to remove — caller falls back to the original
    ctx.putImageData(imageData, 0, 0);
    return canvas;
  }

  // Flags a design as likely needing a designer's attention when the FINAL
  // (post crop/removal) artwork has no transparency anywhere at all. Chosen
  // over trying to detect "did removeBackground do a good job" directly,
  // because that's ambiguous (an already-clean transparent PNG legitimately
  // has bgRemoved=false and nothing to flag) — whereas "the file we're
  // about to print is 100% opaque, edge to edge" is unambiguous: a real
  // transfer design almost never legitimately fills its whole canvas with
  // no transparent area at all, and this is exactly the shape of failure
  // confirmed for real (a screenshot with a solid black plate the corner-
  // sampling wand couldn't identify as background — 2026-08-02).
  // Sparse-sampled for speed on large uploads, same pattern as
  // detectContentBBox's own hasAlpha check.
  // A minimum bar, not "does any transparency exist at all" — a real
  // design that's had its background genuinely stripped almost always ends
  // up with a SUBSTANTIAL transparent area, not just a thin sliver. Using
  // "any transparency at all" as the bar was too easy to satisfy: a crop
  // that includes even a few padding pixels of a pre-existing transparent
  // margin (present in the ORIGINAL file, nothing to do with whether the
  // actual design background got removed) would pass, silently missing
  // exactly the case this exists to catch — 2026-08-02's real CULPRIT
  // file, which had a thin transparent strip below an entirely opaque,
  // never-stripped black plate.
  var MIN_TRANSPARENT_FRACTION = 0.12;

  function hasAdequateTransparency(imgOrCanvas) {
    var w = imgOrCanvas.naturalWidth || imgOrCanvas.width;
    var h = imgOrCanvas.naturalHeight || imgOrCanvas.height;
    if (!w || !h) return true; // unknown — don't false-flag on a measurement failure

    var canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(imgOrCanvas, 0, 0, w, h);

    var data;
    try {
      data = ctx.getImageData(0, 0, w, h).data;
    } catch (e) {
      return true; // tainted canvas — can't check, don't false-flag
    }

    var sampled = 0, transparent = 0;
    var stride = 4 * 23; // denser than detectContentBBox's own sampling — this needs an actual ratio, not just a yes/no hit
    for (var i = 3; i < data.length; i += stride) {
      sampled++;
      if (data[i] < 10) transparent++;
    }
    if (!sampled) return true;
    return (transparent / sampled) >= MIN_TRANSPARENT_FRACTION;
  }

  // Finds the bounding box of "real" content in an uploaded image, so a
  // design with lots of unused transparent (or flat background-colour)
  // padding around it can be trimmed down to its true size before we use
  // it for DPI/print-size math. Returns null when there's nothing worth
  // trimming (already tight, or the file is entirely blank).
  function detectContentBBox(img) {
    var w = img.naturalWidth || img.width;
    var h = img.naturalHeight || img.height;
    if (!w || !h) return null;

    var canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);

    var data;
    try {
      data = ctx.getImageData(0, 0, w, h).data;
    } catch (e) {
      return null; // tainted canvas — skip trimming rather than fail the upload
    }

    // Sample density scales with image size to keep this fast on large files.
    var step = Math.max(1, Math.floor(Math.min(w, h) / 900));

    var hasAlpha = false;
    for (var i = 3; i < data.length; i += 4 * 211) {
      if (data[i] < 250) { hasAlpha = true; break; }
    }

    var isBackground;
    if (hasAlpha) {
      isBackground = function (idx) { return data[idx + 3] < 10; };
    } else {
      // No transparency — assume a flat background matching the corners.
      var corners = [[0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1]];
      var br = 0, bg = 0, bb = 0;
      corners.forEach(function (c) {
        var idx = (c[1] * w + c[0]) * 4;
        br += data[idx]; bg += data[idx + 1]; bb += data[idx + 2];
      });
      br /= 4; bg /= 4; bb /= 4;
      var threshold = 18;
      isBackground = function (idx) {
        var dr = data[idx] - br, dg = data[idx + 1] - bg, db = data[idx + 2] - bb;
        return Math.sqrt(dr * dr + dg * dg + db * db) < threshold;
      };
    }

    var minX = w, minY = h, maxX = -1, maxY = -1;
    for (var y = 0; y < h; y += step) {
      for (var x = 0; x < w; x += step) {
        var idx = (y * w + x) * 4;
        if (!isBackground(idx)) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return null; // nothing detected — blank file, leave as-is

    // Small safety margin so anti-aliased edges don't get clipped.
    var pad = Math.round(Math.min(w, h) * 0.01);
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(w - 1, maxX + pad);
    maxY = Math.min(h - 1, maxY + pad);

    var bboxW = maxX - minX + 1;
    var bboxH = maxY - minY + 1;

    // Not enough padding to bother trimming.
    if (bboxW * bboxH > w * h * 0.97) return null;

    return { x: minX, y: minY, width: bboxW, height: bboxH, sourceCanvas: canvas };
  }

  function cropToCanvas(bbox) {
    var out = document.createElement('canvas');
    out.width = bbox.width;
    out.height = bbox.height;
    var ctx = out.getContext('2d');
    ctx.drawImage(bbox.sourceCanvas, bbox.x, bbox.y, bbox.width, bbox.height, 0, 0, bbox.width, bbox.height);
    return out;
  }

  function dataURLtoBlob(dataUrl) {
    var parts = dataUrl.split(',');
    var mime = parts[0].match(/:(.*?);/)[1];
    var bin = atob(parts[1]);
    var arr = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
  }

  function debounce(fn, ms) {
    var t;
    return function () { clearTimeout(t); t = setTimeout(fn, ms); };
  }

  // Turns a raw sideId ("left_sleeve") into display text ("Left Sleeve").
  // Single-word ids (front/back) behave exactly as before.
  function capitalise(s) {
    return s.split('_').map(function (w) { return w.charAt(0).toUpperCase() + w.slice(1); }).join(' ');
  }

  function cssEscape(s) {
    return (window.CSS && CSS.escape) ? CSS.escape(s) : s.replace(/([\[\]])/g, '\\$1');
  }
})();
